import { projectUploadObservation, type UploadSessionFacts } from "../../../types/uploadEvidence.js";
import {
	classifyUploadFailure,
	decideUploadWork,
	computeRetryAt,
	computeSlowTierRetryAt,
	DEFAULT_MAX_ACTIVE_ATTEMPTS,
	DEFAULT_RETRY_MAX_DELAY_MS,
	isAuthUploadFailure,
	isBrowserOffline,
	isOfflineUploadFailure,
	formatAuthGatedUploadFailureReason,
	PERSISTED_TERMINAL_REJECTION_CODE,
	UPLOAD_SETTLING_EXPIRED_CODE,
	type UploadAuthFailureEvidence,
	type UploadFailureClassification
} from "./retryPolicy";
import { SETTLING_CONFLICT_CEILING_MS, withSettlingCeiling } from "./settlingCeiling";
import { noopUploadAuthGate, type UploadAuthGate } from "./authGate";
import { isValidBrowserSignedHeaders } from "./targetInitiation";
import { isValidPresignedUrl } from "./multipartValidation";
import { createUploadDrainEngine, type UploadDrainEngine, type UploadDrainWakePolicy } from "./drainEngine";
import { newUploadId } from "./uploadId";
import { readBlobBytes } from "./blobBytes";
import { CHUNK_SIZE } from "./chunkSize";
import {
	prepareKnownSizeUploadSource,
	createKnownSizeUploadTarget,
	completeKnownSizeUpload,
	knownSizePartReceipt,
	readKnownSizeUploadReceipt
} from "./knownSizeUploadFlow";
import {
	isUploadControlUnavailable,
	uploadControlError,
	readUploadKnownSizeControl,
	uploadObjectControlColumns,
	type UploadObjectControlRow
} from "./uploadKnownSizeControl";
import { isUploadAlreadyCompleteAtCreate } from "./uploadFacadeErrors";
import type { UploadsAnyCreateRequest, UploadsFacadeClient } from "./uploadsFacadeClient";
import type { DurableUploadTransaction } from "./ports";
import { withPersistTimeout } from "./persistTimeout";
import { computePoisonPacedUntil, createPoisonBudget, type PoisonBudget, type PoisonBudgetConfig } from "./poisonBudget";
import {
	hasPersistedUploadFacadeErrorCode,
	isUploadTerminalFailure,
	isUploadTerminalRefusal,
	withFacadeErrorCodeInMessage,
	readUploadFacadeErrorCode,
	readUploadFacadeErrorDetails
} from "./uploadFacadeErrors";
import { isTransportShapedS3Failure, UploadCancelledError } from "./uploadChunk";

export const DEFAULT_SMALL_FILE_PERSIST_TIMEOUT_MS = 15_000;
export const DEFAULT_RECEIPT_SEAL_POLL_MS = 400;
export const DEFAULT_RECEIPT_SETTLING_RETRY_MS = 1_500;
/**
 * LSP-19929: a completion receipt whose registry identity the server definitively no longer holds —
 * bytes it had acknowledged as complete. Nothing local can repair that (re-minting the same
 * idempotency key is answered as a replay of the finalized attempt), so once a host has seen it often
 * enough to rule out a blip (`completionReceiptAbsentStrikes`) it is an evidence terminal: the row
 * parks as needs-attention and the interactive waiter is told, instead of re-reading a 404 forever.
 */
export const COMPLETION_RECEIPT_LOST_ERROR_CODE = "UPLOAD_COMPLETION_RECEIPT_LOST";
export const AMBIGUOUS_RECEIPT_ERROR_CODE = "UPLOAD_RECEIPT_AMBIGUOUS";
export const AMBIGUOUS_RECEIPT_ERROR_MESSAGE = "Direct PUT receipt is not authoritative; retaining local bytes";
export const RECEIPT_CONFIRMATION_PENDING_ERROR_CODE = "UPLOAD_RECEIPT_PENDING";

function isLocalCheckpointFailure(error: unknown, record: SmallFileRecord, receiptOracle: SmallFileLaneOptions["receiptOracle"]): boolean {
	if (!error || typeof error !== "object") return false;
	const candidate = error as { name?: unknown; message?: unknown };
	return (
		Boolean((record.target || record.completionReceipt || record.objectAttempt) && typeof receiptOracle === "function") &&
		(["AbortError", "InvalidStateError", "NotFoundError", "QuotaExceededError", "UnknownError"].includes(String(candidate.name)) ||
			(typeof candidate.message === "string" && candidate.message.includes("IndexedDB")))
	);
}

/**
 * The four receipt states, in the order the lane trusts them:
 * - `received`  the sealed, current-identity object exists. The ONLY state permitting terminalization.
 * - `absent`    the server definitively does not hold this registry identity, so the bytes were never
 *               accepted and a reload may safely re-PUT them.
 * - `pending`   current identity, still on its way to `ready`. Never a failure; the lane re-checks it
 *               inside a bounded budget rather than parking a healthy upload.
 * - `unknown`   the answer is not authoritative (read failed, contradicted/rotated identity, terminal
 *               non-ready status). Retain the bytes and never re-PUT through the ambiguity.
 */
export type SmallFileReceiptState = "received" | "absent" | "pending" | "unknown";

/**
 * One query shape for both checkpoint flavours. `kind` is what the checkpoint IS, not what phase the
 * lane is in: `target` is a signed PUT envelope's identity, `completion` a targetless ready receipt.
 */
export type SmallFileReceiptQuery =
	| {
			record: SmallFileRecord;
			checkpoint: SmallFileTargetCheckpoint;
			kind: "target";
	  }
	| {
			record: SmallFileRecord;
			checkpoint: SmallFileCompletionReceiptCheckpoint;
			kind: "completion";
	  };

/** Registry statuses meaning "still on its way to `ready`", not "this upload failed". */
export const FINALIZING_RECEIPT_STATUSES: ReadonlySet<string> = new Set(["requested", "uploading", "sealed", "processing"]);

/**
 * A definitive "this registry identity does not exist" answer from any of the three app clients
 * (`status` from the shared facade client and Live's `_handleResponse`, `httpStatus` from Admin's).
 * This is the one error that proves absence rather than merely failing to prove presence, so it is
 * the only error a receipt oracle may map to `absent`.
 */
export function isRegistryNotFoundError(error: unknown): boolean {
	if (typeof error !== "object" || error === null) return false;
	const candidate = error as { status?: unknown; httpStatus?: unknown };
	return candidate.status === 404 || candidate.httpStatus === 404;
}

/**
 * Shared classification of one direct-PUT registry projection against the exact attempt identity the
 * client holds. Identity is checked BEFORE status so a rotated attempt or a foreign row is answered
 * immediately and is never polled as though it were merely pending.
 */
export function classifyDirectPutReceipt(
	projection: unknown,
	expected: SmallFileCompletionReceiptCheckpoint,
	expectedKind = "image"
): SmallFileReceiptState {
	if (!projection || typeof projection !== "object") return "unknown";
	const receipt = projection as {
		id?: unknown;
		kind?: unknown;
		transport?: unknown;
		status?: unknown;
		facts?: UploadSessionFacts | null;
		attempt?: {
			id?: unknown;
			generation?: unknown;
			transport?: unknown;
		} | null;
	};
	const identityMatches =
		receipt.id === expected.registryId &&
		receipt.kind === expectedKind &&
		receipt.transport === "direct-put" &&
		receipt.attempt?.id === expected.attemptId &&
		receipt.attempt.generation === expected.generation &&
		receipt.attempt.transport === "direct-put";
	if (!identityMatches) return "unknown";
	if (receipt.facts !== undefined) {
		const observed = projectUploadObservation({ takenAt: new Date(), registry: { facts: receipt.facts } });
		if (observed.outputReady) return "received";
		return observed.status !== null && FINALIZING_RECEIPT_STATUSES.has(observed.status) ? "pending" : "unknown";
	}
	if (receipt.status === "ready") return "received";
	// A terminal non-ready status (failed/cancelled/abandoned) is NOT `absent`: the bytes may already
	// have landed, and re-PUTting a discarded upload would resurrect deliberately removed media.
	return typeof receipt.status === "string" && FINALIZING_RECEIPT_STATUSES.has(receipt.status) ? "pending" : "unknown";
}

export interface SmallFileObjectControl extends UploadObjectControlRow {
	objectAttempt?: SmallFileCompletionReceiptCheckpoint;
	/** Server snapshotId; named for the existing Live publication protocol. */
	savedSnapshotId?: string;
}

/** Stores supply their source/owner CAS; this owns the immutable protocol and attempt comparison. */
export function selectSmallFileObjectControl(current: SmallFileObjectControl, choice: SmallFileObjectControl): SmallFileObjectControl {
	readUploadKnownSizeControl(current);
	const proposed = readUploadKnownSizeControl(choice);
	const selected =
		current.contractVersion === 2 && proposed?.objectId
			? uploadObjectControlColumns({ contractVersion: 2, objectUpload: proposed }, current)
			: current.contractVersion !== undefined || !current.protocolSelectionPending
				? {
						contractVersion: current.contractVersion,
						protocolSelectionPending: current.protocolSelectionPending,
						objectUploadJson: current.objectUploadJson
					}
				: choice;
	const attempt = choice.objectAttempt ?? current.objectAttempt;
	if (attempt) {
		try {
			requireCompletionReceiptIdentity(attempt);
		} catch (error) {
			throw uploadControlError("Saved object attempt is unreadable", error);
		}
		if (selected.contractVersion !== 2) throw uploadControlError("Object attempt cannot belong to a legacy source");
		if (
			!attempt.takeGeneration ||
			(current.objectAttempt &&
				["registryId", "attemptId", "generation", "takeGeneration"].some(
					(key) =>
						current.objectAttempt![key as keyof SmallFileCompletionReceiptCheckpoint] !==
						attempt[key as keyof SmallFileCompletionReceiptCheckpoint]
				))
		)
			throw uploadControlError("Saved upload attempt binding changed");
	}
	if (current.savedSnapshotId && choice.savedSnapshotId && current.savedSnapshotId !== choice.savedSnapshotId)
		throw uploadControlError("Saved snapshot binding changed");
	const savedSnapshotId = current.savedSnapshotId ?? choice.savedSnapshotId;
	return { ...selected, ...(attempt ? { objectAttempt: attempt } : {}), ...(savedSnapshotId ? { savedSnapshotId } : {}) };
}

export interface SmallFileRecord extends SmallFileObjectControl {
	id: string;
	conversationId?: string;
	organizationId?: string;
	payload: Blob;
	fileName: string;
	fileType: string;
	category: "image" | "document";
	projectChecklistItemId?: string;
	idempotencyId?: string;
	metadata?: Record<string, unknown>;
	createdAt: number;
	retryAt?: number | null;
	retryAttempt?: number;
	/** Persisted failure evidence used to keep auth-gated pacing out of generic lifecycle acceleration. */
	error?: unknown;
	/**
	 * R-2 adds `auth-gated`: schedulable, byte-intact, budget-free, "waiting for a credential we do
	 * not currently hold". It is written only by a store that opts in via
	 * `SmallFileLaneOptions.durableAuthGatedStatus` — see that option for why.
	 */
	status?: "pending" | "uploaded" | "needs-attention" | "auth-gated";
	/** Live snapshots retain this durable outbox state until server completion succeeds. */
	publicationState?: "pending" | "published";
	/** Immutable facade target checkpoint. Legacy direct-put rows deliberately omit this. */
	target?: SmallFileTargetCheckpoint;
	/** Exact ready receipt persisted when idempotent create returns no PUT target. */
	completionReceipt?: SmallFileCompletionReceiptCheckpoint;
	/**
	 * When this item first entered its current server-settling episode: either target admission
	 * returned `UPLOAD_OPERATION_IN_PROGRESS`, or its exact receipt was positively `pending`.
	 * Only stores that keep the whole record durably use this field; hosts with their own row schema
	 * (contact-admin, contact-async) persist the same instant in their own column and expose it
	 * through the hooks below instead.
	 */
	settlingConflictSince?: number;
}

export interface SmallFileCompletionReceiptCheckpoint {
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
}

/** P-4 identity is persisted before dispatch, never reconstructed from a URL on reload. */
export interface SmallFileTargetCheckpoint {
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
	targetId: string;
	url: string;
	headers: Readonly<Record<string, string>>;
	expiresAt: string;
	expectedBytes: number | null;
	expectedDigest: string | null;
	/**
	 * LSP-20795: the saved `Snapshot.id` an IMAGE session is bound to, from the `/uploads` create
	 * projection's `snapshotId`. Live snapshots persist it so the visitor can tell the host which
	 * database row the photo became (the realtime snapshot id is never stored server-side). Absent for
	 * every other lane and for an older API.
	 */
	savedSnapshotId?: string;
}

export interface SmallFileStore {
	/** Source choice and object mapping use the same immutable row/owner fence as other checkpoints. */
	checkpointObjectUpload?(
		id: string,
		choice: SmallFileObjectControl,
		expected: SmallFileRecord,
		transaction?: DurableUploadTransaction
	): Promise<SmallFileObjectControl | undefined>;
	put(record: SmallFileRecord): Promise<void>;
	get?(id: string): Promise<SmallFileRecord | undefined>;
	listPending(): Promise<SmallFileRecord[]>;
	/** Returns false when a newer replacement has superseded `expected`. */
	markUploaded(id: string, expected?: SmallFileRecord, transaction?: DurableUploadTransaction): Promise<boolean | void>;
	/** Optional durable completion fence for lanes that must publish after the PUT. */
	markPublished?(id: string, expected?: SmallFileRecord, transaction?: DurableUploadTransaction): Promise<boolean | void>;
	updateRetry(
		id: string,
		update: {
			retryAt: number | null;
			retryAttempt: number;
			status?: "pending" | "uploaded" | "needs-attention" | "auth-gated";
			error?: unknown;
		},
		expected?: SmallFileRecord,
		transaction?: DurableUploadTransaction
	): Promise<boolean | void>;
	/** Atomically stamps a full facade envelope under the record's current writer epoch. */
	checkpointTarget?(
		id: string,
		target: SmallFileTargetCheckpoint,
		expected?: SmallFileRecord,
		transaction?: DurableUploadTransaction
	): Promise<boolean | void>;
	/**
	 * The durable half of the server-settling ceilings (`settlingCeiling.ts` and receipt confirmation).
	 *
	 * A store that implements BOTH of these opts its rows into the ceiling on the server's
	 * retryable `UPLOAD_OPERATION_IN_PROGRESS` verdict and confirmed `pending` receipts; a store that
	 * implements neither retains ordinary charged pacing, because there is nowhere reload-surviving
	 * to bound a short-poll window. They are separate from
	 * `checkpointTarget` above because the window is store-private bookkeeping rather than part of
	 * the item's upload identity.
	 *
	 * Reads the instant this row's settling window opened, or `undefined` when it has never opened.
	 */
	readSettlingWindow?(id: string): Promise<number | undefined>;
	/** Stamps the instant this row's settling window opened. See `readSettlingWindow` above. */
	armSettlingWindow?(id: string, openedAt: number, expected?: SmallFileRecord): Promise<void>;
	/**
	 * Discards the window after a presign succeeds or a pending receipt is resolved, so the next
	 * settling episode is measured from its own first verdict rather than from one the server has
	 * already resolved. A store that arms but cannot clear keeps the ceiling — a stranded window is
	 * less dangerous than an unbounded loop — but it is a gap, so every in-repo host implements it.
	 */
	clearSettlingWindow?(id: string, expected?: SmallFileRecord): Promise<void>;
	/** Atomically stamps the exact ready receipt before an already-complete row is terminalized. */
	checkpointCompletionReceipt?(
		id: string,
		receipt: SmallFileCompletionReceiptCheckpoint,
		expected?: SmallFileRecord,
		transaction?: DurableUploadTransaction
	): Promise<boolean | void>;
	/** Explicit, audited recovery: preserve the anomaly and create a new local retry item. */
	recoverAnomalous?(
		expected: SmallFileRecord,
		replacement: SmallFileRecord,
		audit: SmallFileAnomalyRecoveryAudit,
		transaction?: DurableUploadTransaction
	): Promise<boolean | void>;
}

export interface SmallFileAnomalyRecoveryAudit {
	requestedBy: "user" | "support";
	reason: string;
	requestedAt: number;
	correlationId?: string;
}

export interface SmallFileWire {
	presign(input: {
		id?: string;
		conversationId: string;
		fileDetails: Array<{
			originalFileName: string;
			fileExtension: string;
			fileType: string;
			idempotencyId?: string;
		}>;
		type: "image" | "document";
		projectChecklistItemId?: string;
		metadata?: Record<string, unknown>;
		expectedBytes?: number;
		/**
		 * Memoized SHA-256 hex of the record payload for this drain attempt. A wire that
		 * must send the digest to the server uses this so the lane's own payload-evidence
		 * check reuses the same computation instead of digesting the Blob a second time.
		 */
		payloadDigest?: () => Promise<string>;
	}): Promise<string | SmallFilePresignTarget>;
}

export interface SmallFilePresignTarget {
	url?: string;
	headers?: Readonly<Record<string, string>>;
	alreadyComplete?: boolean;
	/** Exact current receipt returned by an idempotent create replay with no new PUT target. */
	alreadyCompleteReceipt?: SmallFileCompletionReceiptCheckpoint;
	/** Optional full target-envelope facts for direct PUT callers. */
	expiresAt?: string;
	expectedBytes?: number | null;
	expectedDigest?: string | null;
	/** Full P-4 target identity is required for an uploads-family direct PUT. */
	registryId?: string;
	attemptId?: string;
	generation?: number;
	takeGeneration?: string;
	targetId?: string;
	/** LSP-20795: see SmallFileTargetCheckpoint.savedSnapshotId; carried into the checkpoint only when present. */
	savedSnapshotId?: string;
}

export interface SmallFilePutTransport {
	put(
		url: string,
		payload: Blob,
		options: {
			signal: AbortSignal;
			headers?: Readonly<Record<string, string>>;
			logContext?: Readonly<Record<string, unknown>>;
			/** Require a server receipt before the durable Blob may be deleted. */
			requireServerReceipt?: boolean;
		}
	): Promise<{ status: number }>;
}

/**
 * Optional per-record ownership fence. The durable adapter owns the actual
 * lease implementation; this generic lane only refuses a stale completion.
 */
export interface SmallFileUploadClaim {
	isCurrent(): Promise<boolean>;
	/** Refresh an expiring writer lease before an external operation, if needed. */
	renew?(): Promise<boolean>;
	/**
	 * Runs a local mutation in the writer's durable owner/epoch transaction. The
	 * callback is not invoked when the writer has been replaced or its lease
	 * expired.
	 */
	commitIfWriterOwns?<T>(effect: (transaction?: DurableUploadTransaction) => Promise<T>): Promise<
		| {
				kind: "applied";
				value: T;
		  }
		| { kind: "skipped-not-owned" }
	>;
	release(): Promise<void>;
}

export interface SmallFileLaneOptions {
	objectUploads?: {
		client(): UploadsFacadeClient;
		createRequest(record: SmallFileRecord): UploadsAnyCreateRequest;
		waitForProcessing?: boolean;
	};
	store: SmallFileStore;
	wire: SmallFileWire;
	transport: SmallFilePutTransport;
	/**
	 * LSP-22874 (owner directive, contact-async only): park a row ONLY on a definitive server refusal
	 * (`isDefinitiveUploadRefusal`). A bare 400/410/413/422, an ambiguous receipt, and bytes accepted but not yet
	 * recorded all retry on the paced backoff instead, because the bytes are still on the device. Off by default so
	 * contact-admin and contact-live keep their existing park rule.
	 */
	parkOnlyDefinitiveRefusals?: boolean;
	persistTimeoutMs?: number;
	maxActiveAttempts?: number;
	/**
	 * R-0: the attempt count at which this item moves from the fast retry tier to the slow
	 * (minutes-capped) one. It is NOT a budget and NOT a terminal condition — an item past this
	 * count keeps retrying forever, just paced so it cannot hot-loop or starve healthy siblings.
	 *
	 * It also supplies the durable poison budget's default ceiling, which under R-0 means the same
	 * thing: the point where pacing starts, not the point where scheduling stops.
	 */
	slowRetryAfterAttempts?: number;
	/** Optional delay for a retry. Returning null re-arms from lifecycle/new-work signals. */
	retryDelayMs?: (attempt: number, nowMs: number) => number | null;
	/**
	 * R-0: offline detection, so an offline failure costs no attempt. The item still gets a durable
	 * shared fallback probe in case the online/visibility wake is missed; the small-file lanes
	 * previously had no offline gate at all, which is
	 * how a connection flap during a Live call burned an entire budget in seconds (F-10).
	 */
	isOffline?: () => boolean;
	now?: () => number;
	random?: () => number;
	onDrainError?: (error: unknown) => void;
	/** Fires once a newly minted target or targetless completion receipt is validated and checkpointed. */
	onTargetAcquired?: (input: { record: SmallFileRecord; target: SmallFilePresignTarget }) => void;
	/**
	 * LSP-19929: declares that this host checkpoints COMPLETION receipts BEFORE the server has reported
	 * `ready` (contact-admin's multipart rotation hands its receipt over the moment `complete` is
	 * acknowledged). Two consequences, both off by default because every other producer of a
	 * completion receipt only checkpoints one from a create replay that already reported `ready`, for
	 * which a later `pending` is a server regression that must stay on the charged path (LSP-19855):
	 *  - a `pending` completion receipt rides the budget-free settling cadence under the durable
	 *    settling ceiling, exactly like a `pending` target receipt after a direct PUT;
	 *  - `absentStrikes` consecutive `absent` verdicts park the row with `UPLOAD_COMPLETION_RECEIPT_LOST`.
	 *    The server's 404 is documented as identical for an absent id, a wrong tenant and a revoked
	 *    upload, and a proxy blip can produce one too, so a single read proves nothing — but a genuinely
	 *    lost registry must not be re-read forever either.
	 */
	completionReceiptFinalizing?: { absentStrikes: number };
	/** Diagnostic hook for a completion that lost its writer before a mutation. */
	onStaleOwner?: (context: { id: string; phase: string }) => void;
	claim?: (record: SmallFileRecord) => Promise<SmallFileUploadClaim | undefined>;
	/**
	 * Optional idempotent server completion. It runs only after `markUploaded`
	 * durably records the payload, then `markPublished` clears the outbox.
	 */
	publish?: (record: SmallFileRecord) => Promise<void>;
	/**
	 * Transport flag, not a receipt state: it tells the direct PUT to require a receipt header
	 * before the durable Blob may be deleted. It answers a different question from `receiptOracle`.
	 */
	requireServerReceipt?: boolean;
	/**
	 * The single receipt oracle. It answers, for one exact checkpointed identity, which of the four
	 * states the server is in; the lane owns identity validation, the bounded `pending` re-check, and
	 * retry scheduling. Required: a lane with no oracle cannot safely terminalize anything, and an
	 * optional hook silently degraded every facade row to ambiguous.
	 */
	receiptOracle: (input: SmallFileReceiptQuery) => Promise<SmallFileReceiptState>;
	/**
	 * Authoritative existence check for the exact checkpointed direct-PUT attempt. A `false` answer
	 * lets a pending receipt reuse its still-valid target; `true` or unavailable keeps polling.
	 */
	targetObjectExists?: (input: { record: SmallFileRecord; checkpoint: SmallFileTargetCheckpoint }) => Promise<boolean | undefined>;
	/**
	 * Bounded budget for re-reading a `pending` TARGET receipt inside one attempt. Direct-PUT
	 * finalization is asynchronous by contract, so a still-finalizing server must not be mistaken for
	 * a failed delivery. Defaults to 0 (a single read) for hosts that answer synchronously or keep
	 * their own wait; the deadline is measured on the real clock, never the injectable `now`.
	 */
	receiptSealWaitMs?: number;
	/** Poll interval inside `receiptSealWaitMs`. Each poll also renews the writer lease. */
	receiptSealPollMs?: number;
	/** Injectable delay for the seal poll; defaults to `setTimeout`. */
	sleep?: (milliseconds: number) => Promise<void>;
	/** Record predicate, not a receipt state: which rows must carry a durable completion receipt. */
	requiresCompletionReceipt?: (record: SmallFileRecord) => boolean;
	/** Some interactive callers keep the bounded refresh retry inside one drain request. */
	retryRefreshImmediately?: boolean;
	/** Durable, item-scoped admission/retry budget. A poisoned item is skipped without blocking siblings. */
	poison?: PoisonBudgetConfig<SmallFileRecord>;
	/**
	 * R-2: this surface's auth-recovery signal. A fired `onAuthRecovered` is a WAKE — the lane drains
	 * immediately with pacing reset for the auth-gated rows, so a successful sign-in resumes uploads
	 * instead of waiting out a 15-minute backoff window. Defaults to the no-op gate, so a lane whose
	 * host is not wired yet behaves exactly as it does today.
	 */
	authGate?: UploadAuthGate;
	/**
	 * R-2: may this lane persist the `auth-gated` STATUS on a row?
	 *
	 * Opt-in, and the reason is a real hazard rather than caution. Each host supplies its own
	 * `store.listPending`, and some implement it as an equality query on the status column
	 * (contact-admin's is `where("status").equals("pending")`). Writing a status such a store does not
	 * enumerate would make the row invisible to every drain — an unschedulable park, which is the
	 * exact defect R-0 and R-2 exist to remove, introduced by the fix for it.
	 *
	 * So the durable status is written only where the host has confirmed its `listPending` admits it
	 * (Live's `isPendingLiveSnapshot` excludes `needs-attention` and nothing else, so it does). Where
	 * it is off, an auth-gated failure still gets every R-2 property that matters — schedulable
	 * (`status: "pending"`), byte-intact, budget-free, paced on the slow tier — it simply cannot be
	 * told apart from an ordinary paced row by status alone. `onAuthGated` fires either way.
	 */
	durableAuthGatedStatus?: boolean;
	/**
	 * R-2: called instead of a failure callback when a row parks as auth-gated, so a surface can
	 * render "Waiting for sign-in" (with a sign-in action where one exists) rather than "Failed" or a
	 * permanent "Retrying…" on an item nothing is retrying.
	 */
	onAuthGated?: (record: SmallFileRecord, error: unknown) => void;
}

const inertWakePolicy: UploadDrainWakePolicy = {
	start: () => undefined,
	stop: () => undefined,
	pendingObserved: () => undefined,
	idleObserved: () => undefined,
	claimMissed: () => undefined
};

function extensionFromMime(fileType: string, fileName: string): string {
	const extension = fileType.split("/")[1]?.split(";")[0]?.trim();
	if (extension) return extension;
	return fileName.split(".").pop()?.toLowerCase() ?? "bin";
}

function targetContractError(message: string, code: string): Error & { code: string; status: number } {
	return Object.assign(new Error(message), { code, status: 422 });
}

/**
 * Target-envelope validation failures are RETRYABLE by creator decision: a transiently
 * malformed server presign must leave the durable row pending and retry within the existing
 * budget, never park it as needs-attention with no user recovery path. Carrying no HTTP
 * status keeps `classifyUploadFailure` off its generic 4xx terminal rule, matching the plain
 * errors the shared `validateUploadTargetEnvelope` throws. `TARGET_EXPIRED` keeps its own
 * `refresh` classification via the marker check in `retryPolicy.ts` and still uses this helper.
 */
function retryableTargetContractError(message: string, code: string): Error & { code: string } {
	return Object.assign(new Error(message), { code });
}

/** Memoizes the payload bytes so WebKit never has to stream a persisted Blob directly. */
function createPayloadBytesOnce(payload: Blob): () => Promise<ArrayBuffer> {
	let pending: Promise<ArrayBuffer> | undefined;
	return () => (pending ??= readBlobBytes(payload));
}

/** Memoizes the payload SHA-256 so one drain attempt digests each record exactly once. */
export function createPayloadDigestOnce(payload: Blob, readPayload = createPayloadBytesOnce(payload)): () => Promise<string> {
	let pending: Promise<string> | undefined;
	return () => (pending ??= computeSha256Hex(readPayload));
}

async function computeSha256Hex(readPayload: () => Promise<ArrayBuffer>): Promise<string> {
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) throw retryableTargetContractError("Small-file target digest cannot be verified", "TARGET_DIGEST_UNAVAILABLE");
	let digest: ArrayBuffer;
	try {
		digest = await subtle.digest("SHA-256", await readPayload());
	} catch {
		throw retryableTargetContractError("Small-file target digest cannot be verified", "TARGET_DIGEST_UNAVAILABLE");
	}
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function isExactOpaqueIdentity(value: unknown): value is string {
	return typeof value === "string" && value.length > 0 && value.trim() === value;
}

function requireFacadeTargetIdentity(
	target: SmallFilePresignTarget
): asserts target is SmallFilePresignTarget &
	Required<Pick<SmallFilePresignTarget, "registryId" | "attemptId" | "generation" | "targetId" | "url" | "expiresAt">> {
	if (
		!isExactOpaqueIdentity(target.registryId) ||
		!isExactOpaqueIdentity(target.attemptId) ||
		typeof target.generation !== "number" ||
		!Number.isSafeInteger(target.generation) ||
		target.generation < 1 ||
		!isExactOpaqueIdentity(target.targetId) ||
		typeof target.url !== "string" ||
		typeof target.expiresAt !== "string"
	) {
		throw retryableTargetContractError(
			"Facade small-file target must carry its complete immutable identity",
			"TARGET_IDENTITY_INVALID"
		);
	}
}

function requireCompletionReceiptIdentity(receipt: SmallFileCompletionReceiptCheckpoint): SmallFileCompletionReceiptCheckpoint {
	if (
		!isExactOpaqueIdentity(receipt.registryId) ||
		!isExactOpaqueIdentity(receipt.attemptId) ||
		!Number.isSafeInteger(receipt.generation) ||
		receipt.generation < 1
	) {
		throw targetContractError("Small-file completion receipt has invalid identity", "RECEIPT_IDENTITY_INVALID");
	}
	return receipt;
}

/**
 * Payload-evidence half of target validation: size and digest. Kept separate from the
 * URL/header/expiry shape check so one drain attempt digests the payload exactly once
 * even when both a persisted checkpoint and a freshly presigned envelope are validated.
 */
async function validateSmallFilePayloadEvidence(
	target: SmallFilePresignTarget,
	payload: Blob,
	payloadDigest: () => Promise<string>
): Promise<void> {
	if (target.expectedBytes !== undefined && target.expectedBytes !== null) {
		if (!Number.isSafeInteger(target.expectedBytes) || target.expectedBytes < 0) {
			throw retryableTargetContractError("Small-file target expected size is invalid", "TARGET_INVALID");
		}
		if (target.expectedBytes !== payload.size) {
			throw retryableTargetContractError("Small-file target expected size does not match the payload", "TARGET_SIZE_MISMATCH");
		}
	}
	if (target.expectedDigest !== undefined && target.expectedDigest !== null) {
		if (!/^[a-f0-9]{64}$/.test(target.expectedDigest)) {
			throw retryableTargetContractError("Small-file target expected digest is invalid", "TARGET_INVALID");
		}
		if ((await payloadDigest()) !== target.expectedDigest) {
			throw retryableTargetContractError("Small-file target expected digest does not match the payload", "TARGET_DIGEST_MISMATCH");
		}
	}
}

/** URL/header/expiry half of target validation. Carries no payload cost. */
function validateSmallFileTargetShape(target: SmallFilePresignTarget, nowMs: number): void {
	if (target.url !== undefined && !isValidPresignedUrl(target.url)) {
		throw retryableTargetContractError("Small-file target URL is invalid", "TARGET_INVALID");
	}
	if (target.headers !== undefined && !isValidBrowserSignedHeaders(target.headers)) {
		throw retryableTargetContractError("Small-file target headers are invalid", "TARGET_INVALID");
	}
	if (target.expiresAt !== undefined) {
		const expiresAt = Date.parse(target.expiresAt);
		if (!Number.isFinite(expiresAt) || new Date(expiresAt).toISOString() !== target.expiresAt) {
			throw retryableTargetContractError("Small-file target expiry is invalid", "TARGET_INVALID");
		}
		if (!Number.isFinite(nowMs)) {
			throw retryableTargetContractError("Small-file target expiry cannot be checked", "TARGET_CLOCK_INVALID");
		}
		// TARGET_EXPIRED keeps its dedicated `refresh` classification in retryPolicy.ts.
		if (expiresAt <= nowMs) throw retryableTargetContractError("Small-file target has expired", "TARGET_EXPIRED");
	}
}

function facadeTargetCheckpoint(target: SmallFilePresignTarget): SmallFileTargetCheckpoint | undefined {
	const fields = [target.registryId, target.attemptId, target.generation, target.targetId];
	if (fields.every((value) => value === undefined)) return undefined;
	requireFacadeTargetIdentity(target);
	const { registryId, attemptId, generation, takeGeneration, targetId, url, expiresAt } = target;
	return {
		registryId,
		attemptId,
		generation,
		takeGeneration,
		targetId,
		url,
		headers: target.headers ?? {},
		expiresAt,
		expectedBytes: target.expectedBytes ?? null,
		expectedDigest: target.expectedDigest ?? null,
		// LSP-20795: only when present, so every other lane's persisted checkpoint is byte-identical.
		...(target.savedSnapshotId ? { savedSnapshotId: target.savedSnapshotId } : {})
	};
}

/**
 * The one definition of the ambiguous-receipt failure for every direct-PUT lane. It deliberately
 * carries NO HTTP `status`: `classifyUploadFailure` terminalizes generic 4xx, and an ambiguous
 * receipt is retryable by creator decision. `ambiguous: true` is what drives the lane's own
 * "retain the bytes, never re-PUT" terminal rule.
 */
export function ambiguousReceiptError(message = AMBIGUOUS_RECEIPT_ERROR_MESSAGE): Error & { ambiguous: true; code: string } {
	return Object.assign(new Error(message), {
		ambiguous: true as const,
		code: AMBIGUOUS_RECEIPT_ERROR_CODE
	});
}

/** Named guard so callers stop re-deriving the `ambiguous === true` shape check inline. */
export function isAmbiguousReceiptError(error: unknown): error is Error & { ambiguous: true } {
	return typeof error === "object" && error !== null && (error as { ambiguous?: unknown }).ambiguous === true;
}

/** The PUT may have landed; keep polling its exact checkpoint without re-sending or asking the user to act. */
export function receiptConfirmationPendingError(
	state: Exclude<SmallFileReceiptState, "received">,
	receiptKind: SmallFileReceiptQuery["kind"]
): Error & {
	receiptPending: true;
	receiptState: Exclude<SmallFileReceiptState, "received">;
	receiptKind: SmallFileReceiptQuery["kind"];
	code: string;
} {
	return Object.assign(new Error(`Direct PUT receipt is ${state}; waiting for confirmation`), {
		receiptPending: true as const,
		receiptState: state,
		receiptKind,
		code: RECEIPT_CONFIRMATION_PENDING_ERROR_CODE
	});
}

/** LSP-19929: see `COMPLETION_RECEIPT_LOST_ERROR_CODE`. The code rides in the message because durable rows keep only the message. */
export function completionReceiptLostError(): Error & { code: string } {
	return Object.assign(
		new Error(
			`Server no longer holds the registry identity for a completed upload; needs attention [${COMPLETION_RECEIPT_LOST_ERROR_CODE}]`
		),
		{ code: COMPLETION_RECEIPT_LOST_ERROR_CODE }
	);
}

export function isReceiptConfirmationPendingError(error: unknown): error is Error & {
	receiptPending: true;
	receiptState: Exclude<SmallFileReceiptState, "received">;
	receiptKind: SmallFileReceiptQuery["kind"];
} {
	if (typeof error !== "object" || error === null) return false;
	const candidate = error as { receiptPending?: unknown; receiptKind?: unknown };
	return candidate.receiptPending === true && (candidate.receiptKind === "target" || candidate.receiptKind === "completion");
}

/**
 * R-0 one-time startup requeue: what to do with a durable row that is parked as `needs-attention`
 * TODAY, on a real user's device, from the attempt-count rule this directive abolished.
 *
 * Every store that parked rows under the old rule needs this, and the classification is identical
 * everywhere, so it lives here rather than being re-derived per app.
 *
 * - `bytes-gone`  the local payload is missing or empty. Nothing to send; this is one of R-0's four
 *                 surviving EVIDENCE terminals and the row must stay parked (action: Re-add).
 * - `evidence`    the recorded failure is one of the other evidence terminals — a cancellation or an
 *                 ambiguous receipt without an exact checkpoint (bytes may already be on S3; re-sending
 *                 through that ambiguity is forbidden). Stay parked, log the row.
 * - `requeue`     everything else. Return it to the paced retry pool.
 *
 * Requeueing is safe even when the stored error is unreadable: a requeued row re-enters
 * `uploadOne`, whose reload path revalidates the payload evidence and re-reads the receipt oracle
 * BEFORE any network send. A row that is genuinely evidence-terminal re-derives that verdict on its
 * next attempt and re-parks, at the cost of one read; a row parked only because it ran out of
 * attempts gets its life back.
 *
 * IT FAILS OPEN, DELIBERATELY. `bytes-gone` is returned only from POSITIVE proof — the payload field
 * is absent/null, or its byte count is readable and zero. An unreadable payload shape (IndexedDB can
 * hand a Blob back from another realm, where duck-typing and `instanceof` both come up short) is
 * treated as "requeue", because the cost of a wrong `requeue` is one wasted attempt while the cost
 * of a wrong `bytes-gone` is a permanently parked row holding the user's only copy — exactly the
 * defect R-0 exists to remove. The lane's own payload decoder is the real authority on whether the
 * bytes can be sent, and it skips an unusable payload non-terminally.
 *
 * R-2 adds a fourth value: `auth-gated` — "parked waiting for a credential we do not currently hold".
 * Every requeue pass must treat it as a REQUEUE (the verdict re-derives from live credential state on
 * the next attempt); it is a distinct value from `requeue` only so the pass can LOG the redrive and
 * the UI can say "Waiting for sign-in" instead of "Retrying". Deliberately additive: a host that
 * still compares `disposition !== "requeue"` keeps today's behavior and compiles unchanged.
 */
export type ParkedSmallFileDisposition = "requeue" | "evidence" | "bytes-gone" | "auth-gated";

/**
 * The evidence-terminal CODE TOKENS, as they survive in a PERSISTED row where the typed error is long
 * gone and only a message string remains.
 *
 * BREAK-IT ROUND 1 (D2), and the reason this list is shaped the way it is. The markers used to be
 * loose substrings — `/NOT[_ ]?FOUND/`, `/UNAUTHORIZED/`, `/FORBIDDEN/`, `/\bCANCELLED\b/` — matched
 * against the whole sentence. `NOT[_ ]?FOUND` matches inside **ENOTFOUND**, the DNS failure that is
 * the OFFLINE class R-0 says must not even cost an attempt, so every device parked behind a captive
 * portal or a DNS outage stayed parked forever: the one pass written to rescue those rows refused
 * them. "NetworkError: host not found" and "a sibling take was cancelled" did the same thing through
 * ordinary prose.
 *
 * The contract now: an evidence marker matches STRUCTURE, never free prose.
 *  - a CODE TOKEN, delimited so it cannot be a fragment of a longer identifier (`ENOTFOUND` is one
 *    token and is not `NOT_FOUND`), which covers both the bare code and contact-admin's persisted
 *    `sentence [CODE]` stamp; or
 *  - HTTP-STATUS-SHAPED text — a revoked-auth/gone status next to an HTTP-ish word or its own reason
 *    phrase. A message that merely contains the digits ("timed out after 404 ms") is not a verdict.
 *
 * Failing open is the deliberate asymmetry: a wrong `requeue` costs one request, and the row
 * re-derives its verdict from the TYPED error on the next attempt (a real 403 comes back with
 * `status: 403` and `classifyUploadFailure` parks it again). A wrong `evidence` permanently strands
 * the customer's only copy.
 */
/** LSP-20898: contact-api's `RecordingFinalizationConflictResponseError` — the permanent legacy-finish 422. */
export const RECORDING_FINALIZATION_CONFLICT_CODE = "RECORDING_FINALIZATION_CONFLICT";

const EVIDENCE_TERMINAL_CODES = [
	// The multipart/direct-PUT evidence terminals.
	"UPLOAD_LOST",
	// LSP-19929: a completion receipt whose registry identity the server repeatedly no longer holds.
	COMPLETION_RECEIPT_LOST_ERROR_CODE,
	"BYTES_GONE",
	"BYTES-GONE",
	// The typed cancellation markers. `AbortError` is a DOMException name and `UPLOAD_CANCELLED` a
	// code; the English word "cancelled" on its own is prose and no longer parks anything.
	"ABORTERROR",
	"UPLOAD_CANCELLED",
	// A chunk-stream attempt refusing a multipart manifest, or a multipart operation on a
	// non-multipart attempt. Unlike the retired UPLOAD_NOT_FOUND below, this proves a structural
	// disagreement rather than conflating states: an attempt's transport is immutable, the code has
	// a single server producer, and no proxy/WAF prose can forge it. Retrying can never succeed.
	"OPERATION_NOT_SUPPORTED",
	// The attempt fence: the attempt id/generation THIS ROW holds is no longer current, so the bytes
	// it would re-send are addressed to an identity the server has retired. No credential event
	// changes that, which is what separates it from the two codes R-2 removed below.
	"UPLOAD_ATTEMPT_FENCE_INVALID",
	// R-2's original justified addition, and the whole reason the directive calls out server vocabulary:
	// `ORGANIZATION_DELETED` was the positively-confirmed permanent fact the API already emitted
	// (404 + a serialized `errorCode`, `types/OrganizationDeletedUploadError.ts`) — and until now it
	// was read by NO client at all: absent from this set, from admin's classifier and from mobile's
	// `HARD_TERMINAL_REASONS`. Clients invented codes the server never sends and ignored the one it
	// does. R-1 still applies: a justified terminal keeps the bytes and offers a re-add.
	"ORGANIZATION_DELETED",
	// An expired editor invitation cannot become valid again; only the owner can issue a new one.
	"SHARE_EXPIRED",
	PERSISTED_TERMINAL_REJECTION_CODE,
	// A typed 409 rotation verdict is terminal in the active classifier even though generic 409s
	// remain retryable. Preserve that distinction when the durable row is reduced to lastError text.
	"REPLACEMENT_IN_PROGRESS",
	// LSP-19514: the other typed 409. A reused idempotency key with a DIFFERENT immutable intent can
	// never succeed on a re-send, so the live `terminal` verdict has to survive the reduction to
	// lastError text — otherwise the reload/parked-row pass requeues the same permanent conflict.
	"UPLOAD_INTENT_CONFLICT",
	// LSP-19912: the lane's own ceiling on the settling verdict. It is minted only after the item
	// has been observed stuck in the server's `UPLOAD_OPERATION_IN_PROGRESS` window for longer than
	// any real settle, so the reload/parked-row pass must NOT hand the same wedged rotation back to
	// the optimistic retry loop the ceiling exists to end.
	UPLOAD_SETTLING_EXPIRED_CODE,
	AMBIGUOUS_RECEIPT_ERROR_CODE,
	// LSP-20898: the legacy `/recording/finish` 422. The take already carries an immutable
	// finalization fact with a DIFFERENT request fingerprint, so the identical re-send can never be
	// accepted (`recordingFinalizationFact.ts`, `isExactReplay`). Untyped, contact-async read the bare
	// 422 as `retry` and re-sent it ~once a second forever. Like SHARE_EXPIRED it is held out of loose
	// prose matching: it has no legacy rows to honour, and the recording wire stamps `[CODE]` onto the
	// message it persists, so the typed/stamped shapes are the only ones that need to match.
	RECORDING_FINALIZATION_CONFLICT_CODE
];

/**
 * R-2 DELETIONS (owner directive 2026-08-27) — recorded, because "this marker used to exist" is what
 * the historical-row redrive in `describeRetiredEvidenceMarker` has to know:
 *
 *  - the four HTTP-STATUS-SHAPED regexes (`/(?:HTTP|STATUS|CODE)\D{0,3}(?:401|403|404)\b/`,
 *    `401 UNAUTHORIZED`, `403 FORBIDDEN`, `404 NOT FOUND`). Status-shaped prose is ABOLISHED: a
 *    durable verdict is a typed, enumerated reason code or it is no verdict at all. These matched
 *    over `lastError` text that admin's `_handleResponse` persists VERBATIM from non-JSON error
 *    bodies, so a WAF or proxy page containing the words "HTTP 403" permanently parked a healthy row.
 *  - `CAPABILITY_EXPIRED` — an expiry, and one with no producer anywhere in `apps/contact-api/src`
 *    (verified repo-wide). A client-invented code cannot be evidence of anything.
 *  - `URL_REFRESH_AUTH_REJECTED` — an expiry by name: a refresh refused after a BOUNDED renewal.
 *    "We stopped asking" is not "the credential can never come back".
 *  - `UPLOAD_NOT_FOUND` / `NOT_FOUND` — the server documents its 404 as DELIBERATELY identical for
 *    an absent id, a wrong tenant and a no-longer-permitted upload (`types/UploadFacadeError.ts`),
 *    so it cannot prove absence.
 *
 * All six moved to `AUTH_GATED_ERROR_CODES` in `retryPolicy.ts`; the status-shaped regexes moved
 * nowhere, because R-2 rule 4 leaves no legitimate reading for them.
 */
const R2_RETIRED_EVIDENCE_MARKERS: ReadonlyArray<{
	marker: string;
	pattern: RegExp;
	reason: string;
}> = [
	{
		marker: "HTTP-STATUS-SHAPED-PROSE",
		pattern: /(?:HTTP|STATUS|CODE)\D{0,3}(?:401|403|404)\b|\b401\s+UNAUTHORIZED\b|\b403\s+FORBIDDEN\b|\b404\s+NOT\s+FOUND\b/,
		reason: "status-shaped text is no longer a verdict (R-2 rule 4)"
	},
	{
		marker: "CAPABILITY_EXPIRED",
		pattern: /(?:^|[^A-Z0-9_-])CAPABILITY_EXPIRED(?:[^A-Z0-9_-]|$)/,
		reason: "an expiry with no server producer"
	},
	{
		marker: "URL_REFRESH_AUTH_REJECTED",
		pattern: /(?:^|[^A-Z0-9_-])URL_REFRESH_AUTH_REJECTED(?:[^A-Z0-9_-]|$)/,
		reason: "a bounded refresh giving up is not a permanent fact"
	},
	{
		marker: "UPLOAD_NOT_FOUND",
		pattern: /(?:^|[^A-Z0-9_-])UPLOAD_NOT_FOUND(?:[^A-Z0-9_-]|$)/,
		reason: "the server's 404 conflates absent / wrong-tenant / revoked"
	},
	{
		marker: "NOT_FOUND",
		pattern: /(?:^|[^A-Z0-9_-])NOT_FOUND(?:[^A-Z0-9_-]|$)/,
		reason: "the server's 404 conflates absent / wrong-tenant / revoked"
	}
];

/**
 * Which retired marker parked this row, if any — the diagnostic for R-2's historical-row migration.
 *
 * A row parked on one of these was parked by a rule THAT NO LONGER EXISTS, so the requeue passes must
 * return it to the schedulable pool and must SAY SO with its id (root `CLAUDE.md`, "Diagnostic logging
 * when fixing silent failures"). Without this, a redrive is indistinguishable in the logs from a row
 * that was always requeueable, and "did the migration actually reach my stuck snapshot?" becomes
 * unanswerable — which is exactly the archaeology R-2 exists to remove.
 */
export function describeRetiredEvidenceMarker(error: unknown): { marker: string; reason: string } | undefined {
	const text = typeof error === "string" ? error : error instanceof Error ? error.message : "";
	if (!text) return undefined;
	const marker = text.toUpperCase();
	const retired = R2_RETIRED_EVIDENCE_MARKERS.find((candidate) => candidate.pattern.test(marker));
	return retired ? { marker: retired.marker, reason: retired.reason } : undefined;
}

/**
 * `SHARE_EXPIRED` (and, since LSP-20898, `RECORDING_FINALIZATION_CONFLICT`) are deliberately excluded
 * from loose token matching.
 *
 * A durable `lastError` is NOT always a verdict this code authored: the photo lane persists the raw
 * failure text verbatim (`photoSnapshotStore` stores `errorText(update.error)`), so a proxy, WAF or
 * support page whose prose merely names a code reaches the durable row. Every other code here already
 * matched a delimited token before this change, and rows persisted by earlier builds depend on that —
 * contact-admin's requeue gate reads exactly those strings — so their treatment is left alone. The new
 * code has no legacy rows to honour, so it requires the typed or stamped shape and cannot strand a row
 * on somebody else's prose.
 */
const PROSE_EXCLUDED_EVIDENCE_TERMINAL_CODES: ReadonlySet<string> = new Set(["SHARE_EXPIRED", RECORDING_FINALIZATION_CONFLICT_CODE]);
const PROSE_MATCHABLE_EVIDENCE_TERMINAL_CODES = EVIDENCE_TERMINAL_CODES.filter((code) => !PROSE_EXCLUDED_EVIDENCE_TERMINAL_CODES.has(code));

/**
 * A code token is delimited by anything that is not a code character, so it can be the whole message,
 * bracket-stamped (`... [UPLOAD_LOST]`), or sit next to a status (`409 UPLOAD_LOST`) — but never a
 * fragment of a longer identifier. That single boundary rule is what keeps `NOT_FOUND` out of
 * `ENOTFOUND`.
 */
const EVIDENCE_TERMINAL_TEXT = PROSE_MATCHABLE_EVIDENCE_TERMINAL_CODES.map(
	(code) => new RegExp(`(?:^|[^A-Z0-9_-])${code}(?:[^A-Z0-9_-]|$)`)
);

/** The failure shapes that prove an upload can never succeed by retrying the same local bytes. */
/**
 * The two codes that describe the PROJECT rather than one upload: access to the whole conversation is
 * gone, so every sibling in the batch is equally dead and one verdict may speak for all of them.
 *
 * Every other evidence terminal (`UPLOAD_LOST`, `UPLOAD_ATTEMPT_FENCE_INVALID`, `BYTES_GONE`, …) is
 * ITEM-scoped: it proves one upload is finished and says nothing about its siblings. Using one of
 * those as a batch verdict parks a sibling that merely hit a generic 500 with a foreign durable
 * `lastError`, which the requeue guards then refuse to retry — the row never uploads again.
 */
export function isProjectScopedTerminalFailure(error: unknown): boolean {
	const code = readUploadFacadeErrorCode(error);
	if (code) return code === "ORGANIZATION_DELETED" || code === "SHARE_EXPIRED";
	const message = typeof error === "string" ? error : error instanceof Error ? error.message : undefined;
	return (
		message !== undefined &&
		(hasPersistedUploadFacadeErrorCode(message, "ORGANIZATION_DELETED") || hasPersistedUploadFacadeErrorCode(message, "SHARE_EXPIRED"))
	);
}

/**
 * LSP-22874 (owner directive): the evidence terminals that are NOT a server verdict on the upload. `UPLOAD_RECEIPT_AMBIGUOUS`
 * is a 2xx we could not read; `UPLOAD_REJECTED` is the stamp for a bare 400/410/413/422 status (status arithmetic,
 * which a proxy or WAF produces as easily as S3). Neither proves the bytes can never land.
 */
// LSP-22950 D15: `ABORTERROR` is a DOMException NAME, not a server verdict — a local IndexedDB transaction abort throws
// exactly that shape. It stays evidence for `isEvidenceTerminalFailure` (other apps' park rule, unchanged) but never
// definitively refuses an async upload; the typed `UPLOAD_CANCELLED` code and an explicit live cancel still do.
const NON_DEFINITIVE_EVIDENCE_CODES: ReadonlySet<string> = new Set([
	AMBIGUOUS_RECEIPT_ERROR_CODE,
	PERSISTED_TERMINAL_REJECTION_CODE,
	"ABORTERROR"
]);
const DEFINITIVE_REFUSAL_CODES = EVIDENCE_TERMINAL_CODES.filter((code) => !NON_DEFINITIVE_EVIDENCE_CODES.has(code));
const DEFINITIVE_REFUSAL_TEXT = PROSE_MATCHABLE_EVIDENCE_TERMINAL_CODES.filter((code) => !NON_DEFINITIVE_EVIDENCE_CODES.has(code)).map(
	(code) => new RegExp(`(?:^|[^A-Z0-9_-])${code}(?:[^A-Z0-9_-]|$)`)
);

/**
 * LSP-22874 (owner directive): true only when the SERVER definitively refused this upload — a typed refusal it authored
 * (`UPLOAD_LOST`, `UPLOAD_TERMINAL`, `ORGANIZATION_DELETED`, `SHARE_EXPIRED`, the attempt fence, a replacement or
 * intent conflict, the settling ceiling, a finalization conflict), bytes it says are gone, or an explicit cancel.
 *
 * Everything else with bytes on the device is retryable on the async client: an ambiguous S3 receipt, a bare
 * 400/410/413/422 (live status or the `[UPLOAD_REJECTED]` stamp), unsupported-media prose, a missing recording tail.
 * Contrast `isEvidenceTerminalFailure`, which other apps still use as their park rule and is unchanged.
 */
export function isDefinitiveUploadRefusal(error: unknown): boolean {
	if (isUploadTerminalRefusal(error)) return isUploadTerminalFailure(error);
	if (error === undefined || error === null) return false;
	if (isAmbiguousReceiptError(error)) return false;
	const facadeCode = readUploadFacadeErrorCode(error);
	if (facadeCode) return DEFINITIVE_REFUSAL_CODES.includes(facadeCode);
	const message = typeof error === "string" ? error : error instanceof Error ? error.message : undefined;
	if (message) {
		if (DEFINITIVE_REFUSAL_CODES.some((code) => hasPersistedUploadFacadeErrorCode(message, code))) return true;
		if (DEFINITIVE_REFUSAL_TEXT.some((pattern) => pattern.test(message.toUpperCase()))) return true;
	}
	// A live cancellation (not a teardown — the lanes discard their own aborts before asking) is the user's decision.
	// LSP-22950 D15: only an EXPLICIT cancel counts. A bare `AbortError` is what a local IndexedDB transaction abort (or a
	// stray transport abort) throws; it classifies as `cancelled` by name alone, but it proves nothing about the server
	// and is not the user's decision, so the bytes on the device keep retrying.
	return typeof error === "object" && isExplicitUploadCancel(error);
}

function isExplicitUploadCancel(error: object): boolean {
	if (classifyUploadFailure(error, 0).kind !== "cancelled") return false;
	if ((error as { cancelled?: unknown }).cancelled === true || error instanceof UploadCancelledError) return true;
	const { name, code } = error as { name?: unknown; code?: unknown };
	return name !== "AbortError" && code !== "ABORT_ERR" && code !== 20;
}

export function isEvidenceTerminalFailure(error: unknown): boolean {
	if (isUploadTerminalRefusal(error)) return isUploadTerminalFailure(error);
	if (error === undefined || error === null) return false;
	if (isAmbiguousReceiptError(error)) return true;
	// A typed code is the strongest evidence, so it is read first.
	const facadeCode = readUploadFacadeErrorCode(error);
	if (facadeCode && EVIDENCE_TERMINAL_CODES.includes(facadeCode)) return true;
	const message = typeof error === "string" ? error : error instanceof Error ? error.message : undefined;
	if (message) {
		// The stamped shape is authoritative for every code, including the one this change adds: only a
		// terminal writer appends `[CODE]`, and a message that IS the bare code came from a typed source.
		if (EVIDENCE_TERMINAL_CODES.some((code) => hasPersistedUploadFacadeErrorCode(message, code))) return true;
		// Loose token matching keeps exactly the reach it had before this change — see
		// PROSE_MATCHABLE_EVIDENCE_TERMINAL_CODES for why the newly added code is held out of it.
		if (EVIDENCE_TERMINAL_TEXT.some((pattern) => pattern.test(message.toUpperCase()))) return true;
	}
	const classification = classifyUploadFailure(error, 0);
	// A cancellation and a deterministic rejection (400/410/413/422) are evidence; a 408/425/429 is
	// not (`classifyUploadFailure` routes those to `transient`), an unclassifiable error is not under
	// R-0, and a 401/403/404 is not under R-2 — it classifies as `auth` and never reaches here.
	return classification.kind === "cancelled" || classification.kind === "terminal";
}

/**
 * LSP-18802: make a deterministic-rejection verdict survive the reduction to `lastError` text.
 *
 * Every host store keeps a parked row's failure as a MESSAGE STRING and nothing else — contact-async
 * `errorText(update.error)`, contact-admin `persistFailureReason(update.error)`, and contact-live's
 * structured clone of the Error, which drops `status`/`code`. The parked-row sweeps then re-derive the
 * verdict with `classifyParkedSmallFileRow` → `isEvidenceTerminalFailure`, which by R-2 rule 4 reads
 * only typed, enumerated codes: status-shaped prose is NOT a verdict, because admin persists WAF pages
 * verbatim and "HTTP 403" in one of them parked healthy rows forever. So a row this lane parks on
 * `classifyUploadFailure`'s deterministic `400/410/413/422` rule — a typed `status` field the live
 * classifier trusts — reaches the store as `"Uploads facade request failed: HTTP 400"`, the sweep
 * cannot see the verdict in it, and returns the row to the pool. Live says terminal, durable says
 * requeue, and durable runs last: measured on release/100 as one doomed `POST /uploads` per ~30s,
 * forever, with the customer left on "0/1 photo uploaded".
 *
 * The fix is at the writer, not the reader. `PERSISTED_TERMINAL_REJECTION_CODE` (`UPLOAD_REJECTED`)
 * is the code `EVIDENCE_TERMINAL_CODES` already reserves for exactly this — "appended by terminal
 * writers whose typed status must survive reload" — and contact-admin's `FileUploadDialog` has stamped
 * it since LSP-19620. This is the same stamp for the shared lane, applied only when ALL of:
 *  - the live classification is `terminal` (not `cancelled`: an abort is item-terminal, but it is a
 *    teardown, not a rejection of the bytes — LSP-19061 — and `ABORTERROR` is its own marker);
 *  - the persisted string would NOT already re-derive the verdict (a typed facade code the facade
 *    client stamped, `ORGANIZATION_DELETED`, `SHARE_EXPIRED`, … is left exactly as it is);
 *  - the failure is not transport-shaped from the object store (`isTransportShapedS3Failure`): an S3
 *    PUT `400` is AWS's retryable `RequestTimeout`, not a verdict on the request, and stamping it
 *    would strand a healthy photo. The live classifier still parks that shape today and the sweep
 *    still rescues it; both are deliberately left unchanged here.
 *
 * Returns a NEW error rather than mutating the caller's: a frozen or reused error defeats an in-place
 * marker, and provenance must never ride on a mutable property. `status` and `code` are carried
 * across because contact-admin rejects its `uploadSmallFile` waiters with this very object and its
 * callers read both. The original error is NOT attached as `cause` on purpose — `getStatus` follows
 * `cause`, and an outer verdict that wraps an inner error is the shape that once turned a transient
 * 503 around a 404 into a deleted photo.
 */
export function withDurableTerminalRejection(error: unknown, classification: UploadFailureClassification | undefined): unknown {
	if (classification?.kind !== "terminal") return error;
	if (isUploadTerminalFailure(error)) {
		return Object.assign(new Error(withFacadeErrorCodeInMessage(readFailureMessage(error), error), { cause: error }), {
			code: "UPLOAD_TERMINAL",
			details: readUploadFacadeErrorDetails(error)
		});
	}
	// The status the classifier acted on may sit on a WRAPPED error (`getStatus` follows `cause`,
	// `originalError` and `error`), so the transport exclusion has to look wherever the status could
	// have come from — otherwise `new Error("Upload failed", { cause: new S3HttpError(400) })` is
	// terminal by the inner status and stamped by the outer shape, stranding a recoverable upload.
	if (containsTransportShapedS3Failure(error)) return error;
	// What the host store will keep: an Error's message, a string itself, and — contact-admin's
	// `persistFailureReason` for the parsed-JSON rejection its `_handleResponse` throws — the JSON text
	// of a plain object, whose quoted `"REPLACEMENT_IN_PROGRESS"` already matches the delimited code
	// token. If THAT text re-derives the verdict, the row is durable as it stands.
	if (isEvidenceTerminalFailure(durableFailureText(error))) return error;
	const message = readFailureMessage(error);
	// Always a real `Error`: contact-async's `errorText` and contact-admin's `persistFailureReason` both
	// persist `.message` for an Error and something else for anything that is not one (`String(...)`
	// is `"[object Object]"` for a plain body — the stamp would vanish on the way to the row). Every
	// own enumerable field of the original (`status`, `code`, `error`, `errorCode`…) is carried across
	// so a waiter that reads the rejection body still sees what it read before.
	const stampedMessage = `${message} [${PERSISTED_TERMINAL_REJECTION_CODE}]`;
	const stamped = new Error(stampedMessage);
	if (typeof error === "object" && error !== null) {
		for (const [key, value] of Object.entries(error)) {
			// The identity fields stay this Error's own: a duck-typed Error's enumerable `message` would
			// otherwise overwrite the stamp it exists to carry.
			if (key === "message" || key === "name" || key === "stack") continue;
			(stamped as unknown as Record<string, unknown>)[key] = value;
		}
	}
	return stamped;
}

/** Bounded, cycle-safe walk over the same wrapper fields `retryPolicy.getStatus` follows. */
function containsTransportShapedS3Failure(error: unknown): boolean {
	const pending: unknown[] = [error];
	const seen = new Set<unknown>();
	while (pending.length > 0 && seen.size < 8) {
		const current = pending.shift();
		if (typeof current !== "object" || current === null || seen.has(current)) continue;
		seen.add(current);
		if (isTransportShapedS3Failure(current)) return true;
		const details = current as { cause?: unknown; originalError?: unknown; error?: unknown };
		pending.push(details.cause, details.originalError, details.error);
	}
	return false;
}

function readFailureMessage(error: unknown): string {
	if (typeof error === "string") return error;
	if (typeof error === "object" && error !== null) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === "string" && message.length > 0) return message;
	}
	return "Upload rejected";
}

/**
 * The text a host store could persist for this failure — an Error's message, the string itself, or
 * (contact-admin, plain rejection body) its JSON. A duck-typed Error from another realm has both a
 * message and enumerable fields, so both are included; the check only asks whether a typed code is
 * already present, and a superset can only make that answer more often true, never wrongly false.
 */
function durableFailureText(error: unknown): string {
	if (typeof error === "string") return error;
	if (error instanceof Error) return error.message;
	let json = "";
	try {
		json = JSON.stringify(error) ?? "";
	} catch {
		json = "";
	}
	const message = readFailureMessage(error);
	return `${message} ${json}`.trim();
}

/**
 * R-2: is this failure a PAUSE rather than a verdict?
 *
 * Thin on purpose — the evidence tiers live in `retryPolicy.isAuthUploadFailure` so the lanes, the
 * requeue passes and each app's own classifier all read one implementation. Evidence is checked
 * BEFORE this by every caller, because `ORGANIZATION_DELETED` is a coded 404 and the code wins.
 */
export function isAuthGatedFailure(error: unknown, evidence?: UploadAuthFailureEvidence | boolean): boolean {
	return isAuthUploadFailure(error, evidence);
}

/**
 * Reads a parked row's retained payload the way every R-0 requeue pass has to read it, and hands
 * back exactly the two fields `classifyParkedSmallFileRow` judges bytes-presence with.
 *
 * Duck-typed, never `instanceof`: IndexedDB can rehydrate a Blob from another realm, where a strict
 * check is false and would misread intact bytes as `bytes-gone` and leave the row parked forever —
 * the exact failure the requeue passes exist to end. Legacy rows hold an ArrayBuffer and shared-lane
 * rows a Blob, so both byte counts are read (`size` first, then `byteLength`); a payload whose shape
 * is unreadable yields `undefined`, i.e. UNREADABLE rather than absent.
 *
 * `payloadPresent` is positive proof only: it is `false` when the field itself is absent/null, and
 * otherwise left `undefined` so an unreadable shape can never be mistaken for lost bytes.
 */
export function describeParkedPayload(payload: unknown): {
	payloadSize?: number;
	payloadPresent?: boolean;
} {
	if (payload === undefined || payload === null) return { payloadPresent: false };
	const candidate = payload as { size?: unknown; byteLength?: unknown };
	if (typeof candidate.size === "number") return { payloadSize: candidate.size };
	if (typeof candidate.byteLength === "number") return { payloadSize: candidate.byteLength };
	return {};
}

export function classifyParkedSmallFileRow(input: {
	/** Readable byte count of the retained payload. `undefined` means UNREADABLE, not absent. */
	payloadSize?: number;
	/** Pass `false` only when the payload field itself is absent/null — positive proof of loss. */
	payloadPresent?: boolean;
	error?: unknown;
	/** An exact receipt identity makes a legacy ambiguous-receipt park automatically reconcilable. */
	receiptCheckpointPresent?: boolean;
	/** R-2: the host's own credential/host verdict, when it has one. See `isAuthUploadFailure`. */
	authEvidence?: UploadAuthFailureEvidence | boolean;
}): ParkedSmallFileDisposition {
	const sourceUnavailable =
		input.payloadPresent === false ||
		(typeof input.payloadSize === "number" && Number.isFinite(input.payloadSize) && input.payloadSize <= 0);
	const receiptCanReconcile =
		!sourceUnavailable &&
		input.receiptCheckpointPresent &&
		(isAmbiguousReceiptError(input.error) || input.error === AMBIGUOUS_RECEIPT_ERROR_MESSAGE);
	const refused = !sourceUnavailable && !receiptCanReconcile && isEvidenceTerminalFailure(input.error);
	const controlUnavailable = isUploadControlUnavailable(input.error);
	const work = decideUploadWork({
		transferPending: true,
		completionPending: false,
		controlUnavailable,
		sourceUnavailable,
		refused,
		authGated: !sourceUnavailable && !receiptCanReconcile && !refused && isAuthGatedFailure(input.error, input.authEvidence)
	});
	if (work.blockedReason === "source-unavailable") return "bytes-gone";
	if (work.blockedReason === "refused" || work.blockedReason === "control-unavailable") return "evidence";
	return work.blockedReason === "auth-gated" ? "auth-gated" : "requeue";
}

function isExpiredCheckpoint(target: SmallFileTargetCheckpoint, nowMs: number): boolean {
	const expiresAt = Date.parse(target.expiresAt);
	return !Number.isFinite(expiresAt) || expiresAt <= nowMs;
}

/** Who asked `scheduleRetry` for a timer, for the LSP-20948 late-retry diagnostic. */
interface SmallFileRetryScheduleContext {
	source: "failure-write" | "discovery" | "pacing";
	id?: string;
	conversationId?: string;
	/** The clock reading when `retryAt` was chosen; a `retryAt` at or before it asked for no wait. */
	computedAt?: number;
}

/** Direct-PUT lane used by snapshots/photos. It has no multipart target or S3 coordinate state. */
export class SmallFileUploadLane {
	private readonly persistTimeoutMs: number;
	private readonly maxActiveAttempts: number;
	private readonly slowRetryAfterAttempts: number | undefined;
	private readonly abortController = new AbortController();
	private readonly drainEngine: UploadDrainEngine;
	private readonly poison: PoisonBudget<SmallFileRecord>;
	private readonly inFlightUploads = new Map<string, Promise<void>>();
	private readonly authGate: UploadAuthGate;
	private readonly unsubscribeAuthGate: () => void;
	private drainPromise: Promise<void> | undefined;
	private retryTimer: ReturnType<typeof setTimeout> | undefined;
	private retryTimerAt: number | undefined;
	/** LSP-19929: consecutive `absent` completion-receipt reads per row id; see `completionReceiptFinalizing`. */
	private readonly completionReceiptAbsentStrikes = new Map<string, number>();
	private lastRecoveryBackoffClearAtMs: number | undefined;
	private destroyed = false;

	constructor(private readonly options: SmallFileLaneOptions) {
		this.authGate = options.authGate ?? noopUploadAuthGate;
		// R-2 property 2: every lane subscribes to its surface's ONE auth-recovery signal, and on that
		// signal parked items return to the pool immediately with pacing reset. The subscription is torn
		// down in `destroy()` so a lane cannot leak a listener into a long-lived auth store.
		this.unsubscribeAuthGate = this.authGate.onAuthRecovered(() => this.handleAuthRecovered());
		this.persistTimeoutMs = options.persistTimeoutMs ?? DEFAULT_SMALL_FILE_PERSIST_TIMEOUT_MS;
		this.maxActiveAttempts = options.maxActiveAttempts ?? DEFAULT_MAX_ACTIVE_ATTEMPTS;
		this.slowRetryAfterAttempts = options.slowRetryAfterAttempts;
		this.poison = createPoisonBudget<SmallFileRecord>({
			config: options.poison,
			defaultCeiling: () => this.slowRetryAfterAttempts ?? DEFAULT_MAX_ACTIVE_ATTEMPTS,
			now: () => this.now(),
			onError: (_phase, _record, error) => this.options.onDrainError?.(error)
		});
		this.drainEngine = createUploadDrainEngine<SmallFileRecord, number>({
			beforeDrain: async () => this.now(),
			reclaimStartup: async () => undefined,
			discoverPending: (context) => this.discoverPending(context),
			claimDrain: async (_context, run) => run(),
			resumeConcurrency: this.maxActiveAttempts,
			// A snapshot captured while a sibling's PUT is in flight must start in one of the free
			// `maxActiveAttempts` lanes instead of waiting for the in-flight batch to settle. This lane has
			// an inert wake policy, so without mid-flight admission the sibling never starts at all. The
			// durable row id is the stable identity; the per-item writer lease already prevents a
			// double-claim, and this key prevents the engine admitting the same row twice concurrently.
			sessionKey: (record) => record.id,
			resumeSession: async (record) => this.uploadTracked(record),
			wakePolicy: inertWakePolicy,
			onDrainError: (error) => this.options.onDrainError?.(error)
		});
	}

	async enqueue(
		input: Omit<SmallFileRecord, "createdAt" | "status" | "retryAt" | "retryAttempt"> & { createdAt?: number }
	): Promise<void> {
		if (input.payload.size <= 0) throw new Error("Cannot upload empty snapshot payload");
		const record: SmallFileRecord = {
			...input,
			createdAt: input.createdAt ?? this.now(),
			status: "pending",
			retryAt: null,
			retryAttempt: 0
		};
		await withPersistTimeout(this.options.store.put(record), this.persistTimeoutMs, "Small-file persistence");
		// A (re)enqueue is a fresh attempt for this id; it must not inherit an abandoned attempt's strikes.
		this.completionReceiptAbsentStrikes.delete(record.id);
		void this.drain();
	}

	rearm(accelerateBackoff = false): void {
		const ready = accelerateBackoff ? this.accelerateBackoffForRecoveryWake() : Promise.resolve(0);
		void ready
			.catch((error) => this.options.onDrainError?.(error))
			.then(() => this.drain())
			.catch((error) => this.options.onDrainError?.(error));
	}

	/** Lifecycle/user recovery wakes restart paced work on the fast tier, at most once per 30 seconds. */
	async accelerateBackoffForRecoveryWake(): Promise<number> {
		const now = this.now();
		if (this.hostOffline()) return 0;
		if (this.lastRecoveryBackoffClearAtMs !== undefined && now - this.lastRecoveryBackoffClearAtMs < 30_000) return 0;
		const waiting = (await this.options.store.listPending()).filter(
			(record) =>
				record.status !== "needs-attention" &&
				record.status !== "auth-gated" &&
				!this.isAuthGatedRow(record) &&
				typeof record.retryAt === "number" &&
				record.retryAt > now
		);
		if (waiting.length === 0) return 0;
		this.lastRecoveryBackoffClearAtMs = now;
		let cleared = 0;
		for (const record of waiting) {
			const claim = await this.options.claim?.(record);
			try {
				if (this.options.claim && !claim) continue;
				const updated = await this.commitMutation(claim, async (transaction) => {
					const retryUpdated = await this.options.store.updateRetry(
						record.id,
						{
							retryAt: null,
							retryAttempt: 0,
							status: record.status === "uploaded" ? "uploaded" : "pending"
						},
						record,
						transaction
					);
					if (retryUpdated !== false) await this.poison.clearFailure(record, transaction);
					return retryUpdated;
				});
				if (updated !== false) cleared += 1;
			} finally {
				await claim?.release();
			}
		}
		return cleared;
	}

	/**
	 * R-2 property 2: the credential event arrived, so every row that was waiting on one comes back to
	 * the pool NOW rather than at the end of a 15-minute slow-tier window.
	 *
	 * "Pacing reset" is implemented as clearing the auth-gated rows' `retryAt`, not as clearing their
	 * durable poison record: the poison budget protects HEALTHY siblings from an item that keeps
	 * breaking, and re-auth is no evidence about that. Every branch logs — a redrive that reached no
	 * row, or one that could not read the store, is otherwise invisible, and "I signed in and nothing
	 * happened" is the report this port exists to make triagable (root `CLAUDE.md`, "Diagnostic
	 * logging when fixing silent failures").
	 */
	private handleAuthRecovered(): void {
		if (this.destroyed) {
			console.warn("[Upload] Small-file lane ignored an auth-recovery wake after destroy");
			return;
		}
		void this.requeueAuthGatedRows()
			.catch((error) => {
				console.warn("[Upload] Small-file auth-recovery requeue failed; the paced retry still applies", error);
				this.options.onDrainError?.(error);
			})
			.then(() => this.drain())
			.catch((error) => this.options.onDrainError?.(error));
	}

	/** Clear the pacing window on every row this lane parked as auth-gated. Idempotent. */
	private async requeueAuthGatedRows(): Promise<string[]> {
		const rows = await this.options.store.listPending();
		const gated = rows.filter((record) => record.status === "auth-gated" || this.isAuthGatedRow(record));
		if (gated.length === 0) {
			console.info("[Upload] Auth recovered; no auth-gated small-file rows were waiting on it");
			return [];
		}
		const requeued: string[] = [];
		for (const record of gated) {
			try {
				const updated = await this.options.store.updateRetry(
					record.id,
					{
						retryAt: null,
						retryAttempt: record.retryAttempt ?? 0,
						// Same precedence as the failure writer: an `uploaded` row's bytes are already on
						// the server and only its publication is outstanding, so demoting it to `pending`
						// here would re-drive a re-PUT instead of a completion. Clearing `retryAt` is the
						// whole of the redrive for that row.
						status: record.status === "uploaded" ? "uploaded" : "pending"
					},
					record
				);
				if (updated === false) {
					console.warn("[Upload] Auth-gated small-file row changed before its auth-recovery redrive", {
						id: record.id,
						conversationId: record.conversationId
					});
					continue;
				}
				requeued.push(record.id);
				console.info("[Upload] Auth-gated small-file row re-driven after auth recovery", {
					id: record.id,
					conversationId: record.conversationId,
					retryAttempt: record.retryAttempt ?? 0
				});
			} catch (error) {
				console.warn("[Upload] Auth-gated small-file row could not be re-driven", { id: record.id }, error);
			}
		}
		return requeued;
	}

	/** A row whose PERSISTED failure is auth-shaped, for hosts that cannot store the status. */
	private isAuthGatedRow(record: SmallFileRecord): boolean {
		const persistedError = record.error ?? (record as { lastError?: unknown }).lastError;
		return persistedError !== undefined && !isEvidenceTerminalFailure(persistedError) && isAuthGatedFailure(persistedError);
	}

	async drain(now = this.now()): Promise<void> {
		if (this.drainPromise) {
			// The engine's replenishing pool (see `sessionKey` above) re-discovers the moment this
			// request lands and admits the newly persisted sibling into a free lane, so one slow direct
			// PUT does not block a later camera capture from checkpointing its own target. This used to
			// need a lane-local admission bypass, which forked the scheduler and left uploads running
			// past the drain promise it resolved; the engine owns that scheduling now.
			void this.drainEngine.drainNow(`small-file-coalesced-${now}`);
			return this.drainPromise;
		}
		const drainPromise = this.drainEngine.drainNow(`small-file-${now}`).then(() => undefined);
		const trackedDrainPromise = drainPromise.finally(() => {
			if (this.drainPromise !== trackedDrainPromise) return;
			this.drainPromise = undefined;
		});
		this.drainPromise = trackedDrainPromise;
		return trackedDrainPromise;
	}

	private async discoverPending(passStartedAt: number): Promise<SmallFileRecord[]> {
		const pending = (await this.options.store.listPending()).filter((record) => record.status !== "needs-attention");
		// LSP-20948: judge due-ness on the CURRENT clock, not the one read when the pass started. A drain
		// request that lands mid-pass (a retry timer firing while a sibling's long PUT holds the pass open)
		// is re-scanned here; with the pass-start clock the row that just came due read as "not due", and
		// its already-past `retryAt` went straight back to `scheduleRetry` — which dropped it, or, now that
		// a past retry fires on the next task, would loop until the pass ended.
		// A non-finite injected clock falls back to the wall clock, the same fallback `scheduleRetry` uses, so
		// the two never disagree about whether a row is due.
		const finiteReadings = [passStartedAt, this.now()].filter((reading) => Number.isFinite(reading));
		const now = finiteReadings.length > 0 ? Math.max(...finiteReadings) : Date.now();
		const nextRetryAt = pending.reduce<number | null>(
			(earliest, record) =>
				typeof record.retryAt === "number" && Number.isFinite(record.retryAt) && record.retryAt > now
					? Math.min(earliest ?? record.retryAt, record.retryAt)
					: earliest,
			null
		);
		this.scheduleRetry(nextRetryAt, { source: "discovery" });
		const candidates = pending
			// R-0: `needs-attention` is now reachable only from EVIDENCE (bytes gone, ambiguous receipt,
			// a deterministic 400/410/413/422 rejection, `ORGANIZATION_DELETED`), never from an attempt
			// count, so excluding it here no longer strands a perfectly retryable item. Rows parked by the
			// old attempt-count rule are returned to this pool by each app's one-time startup requeue.
			//
			// R-2: `auth-gated` is deliberately NOT excluded — "an auth-gated item is never invisible to
			// every pass". It flows straight through this filter and is admitted the moment its slow-tier
			// window elapses, or immediately when the auth gate fires.
			.filter((record) => this.isRetryDue(record, now));
		// Pacing, not parking: a record over its durable ceiling is skipped only until its pacing
		// window elapses, which keeps one repeatedly failing item from consuming every active slot.
		return this.poison.filterEligible(candidates, now, (pacedUntil, pacedRecord) =>
			this.scheduleRetry(pacedUntil, { source: "pacing", id: pacedRecord.id, conversationId: pacedRecord.conversationId })
		);
	}

	/**
	 * Break-it round 1 (D7): admission used to be `retryAt <= now`, and `NaN <= now` is false forever.
	 * One corrupt persisted number therefore made a row permanently invisible to the drain — no status,
	 * no timer, no log — which is precisely the unschedulable park R-0 abolished, reachable without any
	 * attempt count at all. A retryAt that is not a finite number carries no schedule, so the only
	 * honest reading is "due now"; the corruption is logged because the resulting row cannot show it.
	 */
	private isRetryDue(record: SmallFileRecord, now: number): boolean {
		const retryAt = record.retryAt;
		if (retryAt === undefined || retryAt === null) return true;
		if (!Number.isFinite(retryAt)) {
			console.warn("[Upload] Small-file row has a non-finite retryAt; admitting it as due now", {
				id: record.id,
				conversationId: record.conversationId,
				retryAt,
				retryAttempt: record.retryAttempt
			});
			return true;
		}
		return retryAt <= now;
	}

	// Second dedup layer behind the engine's own in-flight key set: an item is never uploaded twice
	// concurrently even if a future caller reaches `resumeSession` from outside a single drain pass.
	private uploadTracked(record: SmallFileRecord): Promise<void> {
		const existing = this.inFlightUploads.get(record.id);
		if (existing) return existing;
		const upload = this.uploadOne(record).finally(() => {
			if (this.inFlightUploads.get(record.id) === upload) this.inFlightUploads.delete(record.id);
		});
		this.inFlightUploads.set(record.id, upload);
		return upload;
	}

	/** Explicit user/support recovery only; no upload path calls this automatically. */
	async recoverAnomalousItem(id: string, audit: Omit<SmallFileAnomalyRecoveryAudit, "requestedAt">): Promise<SmallFileRecord> {
		const record = await this.options.store.get?.(id);
		if (!record) throw new Error(`Small-file anomaly item ${id} was not found`);
		if (!this.options.store.recoverAnomalous) throw new Error("Small-file anomaly recovery is unavailable for this adapter");
		const replacement: SmallFileRecord = {
			...record,
			id: `${record.id}:recovery:${newUploadId()}`,
			status: "pending",
			retryAt: null,
			retryAttempt: 0,
			metadata: {
				...(record.metadata ?? {}),
				anomalyRecoveryOf: record.id,
				anomalyRecovery: { ...audit, requestedAt: this.now() }
			}
		};
		const claim = await this.options.claim?.(record);
		try {
			if (this.options.claim && !claim) throw new Error("Small-file anomaly recovery could not acquire the writer lease");
			const apply = async (transaction?: DurableUploadTransaction) => {
				const recovered = await this.options.store.recoverAnomalous!(
					record,
					replacement,
					{ ...audit, requestedAt: this.now() },
					transaction
				);
				if (recovered !== false) await this.poison.clearFailure(record, transaction);
				return recovered;
			};
			const outcome = claim?.commitIfWriterOwns ? await claim.commitIfWriterOwns(apply) : { kind: "applied", value: await apply() };
			if (outcome.kind !== "applied" || outcome.value === false) throw new Error("Small-file anomaly recovery lost writer ownership");
		} finally {
			await claim?.release();
		}
		void this.drain();
		return replacement;
	}

	private async uploadOne(record: SmallFileRecord): Promise<void> {
		// One digest per attempt: every payload-evidence check and the presign wire share it.
		const payloadBytes = createPayloadBytesOnce(record.payload);
		const payloadDigest = createPayloadDigestOnce(record.payload, payloadBytes);
		let putSucceeded = false;
		// PERSIST-03: only an abort thrown by the local checkpoint write itself is a failed checkpoint.
		let failedAtLocalCheckpoint = false;
		let contentPersisted = record.status === "uploaded";
		let claim: SmallFileUploadClaim | undefined;
		// Every caller has already seen the server accept the bytes (an accepted PUT or a received receipt).
		const markUploadedAtLocalCheckpoint = async (): Promise<boolean | void> => {
			try {
				return await this.markUploaded(record, claim);
			} catch (checkpointError) {
				failedAtLocalCheckpoint = true;
				if (record.contractVersion === 2 && isLocalCheckpointFailure(checkpointError, record, this.options.receiptOracle)) {
					console.warn("[Upload] Object cleanup checkpoint failed; retaining source and retrying its receipt", {
						id: record.id,
						name: checkpointError instanceof Error ? checkpointError.name : "unknown"
					});
					throw Object.assign(receiptConfirmationPendingError("unknown", "completion"), { cause: checkpointError });
				}
				throw checkpointError;
			}
		};
		try {
			if (
				await this.poison.isPaced(record, undefined, (pacedUntil) =>
					this.scheduleRetry(pacedUntil, { source: "pacing", id: record.id, conversationId: record.conversationId })
				)
			) {
				this.warnUploadEndedEarly("paced-before-claim", record);
				return;
			}
			claim = await this.options.claim?.(record);
			if (this.options.claim && !claim) {
				this.warnUploadEndedEarly("writer-lease-not-acquired", record);
				return;
			}
			// The admission read and writer claim are separate transactions. Recheck after claim so
			// a concurrent owner that has just paced this item wins before network dispatch.
			if (
				await this.poison.isPaced(record, undefined, (pacedUntil) =>
					this.scheduleRetry(pacedUntil, { source: "pacing", id: record.id, conversationId: record.conversationId })
				)
			) {
				this.warnUploadEndedEarly("paced-after-claim", record);
				return;
			}
			if (await this.hasLostUploadOwnership(claim, record, "before-upload")) return;
			if (record.contractVersion === 2 || record.protocolSelectionPending || record.objectUploadJson !== undefined) {
				if (await this.uploadObject(record, claim)) {
					if (this.options.objectUploads?.waitForProcessing) {
						const attempt = record.objectAttempt;
						if (!attempt?.takeGeneration) throw uploadControlError("Processing receipt has no saved attempt");
						const receipt = await this.resolveReceipt(
							{ record, checkpoint: attempt, kind: "completion" },
							claim,
							"object-processing"
						);
						if (receipt !== "received") throw receiptConfirmationPendingError(receipt, "completion");
					}
					const marked = await markUploadedAtLocalCheckpoint();
					if (marked === false) {
						this.warnUploadEndedEarly("mark-uploaded-not-applied-after-object-receipt", record);
						return;
					}
					contentPersisted = true;
					await this.publishOne(record, claim);
					return;
				}
			}
			// `navigator.onLine` is advisory only: it makes an offline failure budget-free below, it never
			// blocks the attempt. A stuck-false flag (Safari after sleep, some WebViews) used to turn the
			// shared probe into a no-op loop that only an `online` event could break.
			if (contentPersisted) {
				// An uploads-family row can crash after the PUT/markUploaded commit but
				// before publication. Its local uploaded bit is not a server receipt: a
				// replacement attempt may now be current. Revalidate the exact durable
				// checkpoint before any callback or deletion on replay.
				if (record.target) {
					requireFacadeTargetIdentity(record.target);
					await validateSmallFilePayloadEvidence(record.target, record.payload, payloadDigest);
					const receipt = await this.resolveReceipt(
						{ record, checkpoint: record.target, kind: "target" },
						claim,
						"uploaded-receipt"
					);
					if (receipt !== "received") throw receiptConfirmationPendingError(receipt, "target");
				} else if (record.completionReceipt) {
					requireCompletionReceiptIdentity(record.completionReceipt);
					const receipt = await this.resolveReceipt(
						{
							record,
							checkpoint: record.completionReceipt,
							kind: "completion"
						},
						claim,
						"uploaded-receipt"
					);
					if (receipt !== "received") throw this.completionReceiptVerdictError(record, receipt);
				} else if (this.options.requiresCompletionReceipt?.(record)) {
					throw ambiguousReceiptError();
				}
				await this.publishOne(record, claim);
				if (!this.hasPublicationTransition()) await this.clearPoisonFailureInCommit(record, claim);
				return;
			}
			if (record.completionReceipt) {
				requireCompletionReceiptIdentity(record.completionReceipt);
				const receipt = await this.resolveReceipt(
					{ record, checkpoint: record.completionReceipt, kind: "completion" },
					claim,
					"reload-receipt"
				);
				if (receipt !== "received") throw this.completionReceiptVerdictError(record, receipt);
				const marked = await markUploadedAtLocalCheckpoint();
				if (marked === false) {
					this.warnUploadEndedEarly("mark-uploaded-not-applied-after-reload-receipt", record);
					return;
				}
				await this.publishOne(record, claim);
				return;
			}
			let targetDetails: SmallFilePresignTarget | undefined;
			if (record.target) {
				// A server receipt authenticates the target identity, not the local Blob. Verify
				// immutable byte evidence first so a corrupt checkpoint can never delete bytes.
				requireFacadeTargetIdentity(record.target);
				await validateSmallFilePayloadEvidence(record.target, record.payload, payloadDigest);
				const priorReceipt = await this.resolveReceipt(
					{ record, checkpoint: record.target, kind: "target" },
					claim,
					"reload-receipt"
				);
				if (priorReceipt === "received") {
					const marked = await markUploadedAtLocalCheckpoint();
					if (marked === false) {
						this.warnUploadEndedEarly("mark-uploaded-not-applied-after-prior-target-receipt", record);
						return;
					}
					await this.publishOne(record, claim);
					return;
				}
				const targetIsCurrent = !isExpiredCheckpoint(record.target, this.now());
				let mayReplayPendingTarget = false;
				if (priorReceipt === "pending" && targetIsCurrent && this.options.targetObjectExists) {
					mayReplayPendingTarget = (await this.options.targetObjectExists({ record, checkpoint: record.target })) === false;
					if (await this.hasLostUploadOwnership(claim, record, "after-object-status")) return;
				}
				if (priorReceipt === "unknown" || (priorReceipt === "pending" && !mayReplayPendingTarget)) {
					throw receiptConfirmationPendingError(priorReceipt, "target");
				}
				if (targetIsCurrent) targetDetails = record.target;
				// The exact receipt says absent and the signed target has expired. Clear only the
				// in-memory selection; the next checkpoint atomically replaces the durable envelope.
				if (!targetDetails) record.target = undefined;
			}
			let newlyAcquired = false;
			if (!targetDetails) {
				const target = await this.presignBoundedBySettlingCeiling(record, payloadDigest);
				// A meeting/tenant can change while presigning. Do not use a URL that
				// was obtained by an owner which is no longer current.
				if (await this.hasLostUploadOwnership(claim, record, "after-presign")) return;
				newlyAcquired = true;
				targetDetails = typeof target === "string" ? { url: target } : target;
				const checkpoint = facadeTargetCheckpoint(targetDetails);
				if (checkpoint) {
					if (!this.options.store.checkpointTarget) {
						throw targetContractError(
							"Facade direct PUT requires durable target checkpoint support",
							"TARGET_CHECKPOINT_REQUIRED"
						);
					}
					const checkpointed = await this.commitMutation(claim, (transaction) =>
						this.options.store.checkpointTarget!(record.id, checkpoint, record, transaction)
					);
					if (checkpointed === false) {
						this.warnUploadEndedEarly("target-checkpoint-not-applied", record, targetDetails);
						return;
					}
					record.target = checkpoint;
				}
			}
			validateSmallFileTargetShape(targetDetails, this.now());
			await validateSmallFilePayloadEvidence(targetDetails, record.payload, payloadDigest);
			if (targetDetails.alreadyComplete) {
				if (record.target) {
					const receipt = await this.resolveReceipt(
						{ record, checkpoint: record.target, kind: "target" },
						claim,
						"already-complete-receipt"
					);
					if (receipt !== "received") throw receiptConfirmationPendingError(receipt, "target");
				} else {
					const completionReceipt = targetDetails.alreadyCompleteReceipt;
					if (!completionReceipt) throw ambiguousReceiptError();
					requireCompletionReceiptIdentity(completionReceipt);
					if (!this.options.store.checkpointCompletionReceipt) {
						throw targetContractError(
							"Targetless completion requires durable receipt checkpoint support",
							"RECEIPT_CHECKPOINT_REQUIRED"
						);
					}
					const checkpointed = await this.commitMutation(claim, (transaction) =>
						this.options.store.checkpointCompletionReceipt!(record.id, completionReceipt, record, transaction)
					);
					if (checkpointed === false) {
						this.warnUploadEndedEarly("completion-receipt-checkpoint-not-applied", record, targetDetails);
						return;
					}
					record.completionReceipt = { ...completionReceipt };
					if (newlyAcquired) this.options.onTargetAcquired?.({ record, target: targetDetails });
					const receipt = await this.resolveReceipt(
						{
							record,
							checkpoint: record.completionReceipt,
							kind: "completion"
						},
						claim,
						"already-complete-receipt"
					);
					if (receipt !== "received") throw this.completionReceiptVerdictError(record, receipt);
				}
				const marked = await markUploadedAtLocalCheckpoint();
				if (marked === false) {
					this.warnUploadEndedEarly("mark-uploaded-not-applied-after-already-complete", record, targetDetails);
					return;
				}
				contentPersisted = true;
				await this.publishOne(record, claim);
				return;
			}
			if (!targetDetails.url) throw new Error("Small-file target did not include a URL");
			if (newlyAcquired) this.options.onTargetAcquired?.({ record, target: targetDetails });
			// Safari can serialize a persisted/disk-backed Blob as an empty request after a tab or
			// network transition even though arrayBuffer() still reads every byte. Send a memory-backed
			// copy and keep the original durable Blob untouched for recovery.
			const uploadPayload =
				typeof record.payload.arrayBuffer === "function"
					? new Blob([new Uint8Array(await payloadBytes())], { type: record.payload.type })
					: record.payload;
			await this.options.transport.put(targetDetails.url, uploadPayload, {
				signal: this.abortController.signal,
				headers: targetDetails.headers,
				logContext: {
					smallFileId: record.id,
					conversationId: record.conversationId,
					category: record.category,
					retryAttempt: record.retryAttempt ?? 0,
					registryId: targetDetails.registryId,
					attemptId: targetDetails.attemptId,
					generation: targetDetails.generation
				},
				// Preserve the existing publication contract while allowing lanes such as
				// Async photos to require a transport receipt without a publish callback.
				requireServerReceipt: this.options.requireServerReceipt ?? Boolean(this.options.publish)
			});
			putSucceeded = true;
			if (record.target) {
				const receipt = await this.resolveReceipt({ record, checkpoint: record.target, kind: "target" }, claim, "post-put-receipt");
				if (receipt !== "received") throw receiptConfirmationPendingError(receipt, "target");
			}
			// A transport may resolve after destroy/lease takeover despite receiving
			// AbortSignal. Never publish or terminalize that stale completion.
			if (await this.hasLostUploadOwnership(claim, record, "after-put")) return;
			const marked = await markUploadedAtLocalCheckpoint();
			if (marked === false) {
				this.warnUploadEndedEarly("mark-uploaded-not-applied-after-put", record, targetDetails);
				return;
			}
			contentPersisted = true;
			// A crash/cancellation here leaves an uploaded+publication-pending row
			// for the next owner; do not invoke a completion callback from a stale lane.
			if (await this.hasLostUploadOwnership(claim, record, "before-publish")) return;
			await this.publishOne(record, claim);
		} catch (error) {
			if (this.destroyed) {
				this.warnUploadEndedEarly("lane-destroyed-during-failure-handling", record);
				return;
			}
			if (!(await this.isClaimCurrent(claim, record.id, "failure", record))) return;
			// Break-it round 1 (D7): a corrupt persisted `retryAttempt` used to flow into `attempt + 1`
			// and out again as a NaN `retryAt`, which no drain pass ever re-admits. Sanitize it here as
			// well as in `computeRetryAt`, so the row's own counter recovers instead of staying corrupt
			// for the rest of its life, and log the corruption while we can still see it.
			const persistedAttempt = record.retryAttempt ?? 0;
			if (!Number.isFinite(persistedAttempt)) {
				console.warn("[Upload] Small-file row has a non-finite retryAttempt; restarting its attempt count", {
					id: record.id,
					conversationId: record.conversationId,
					retryAttempt: record.retryAttempt
				});
			}
			const priorAttempt = Number.isFinite(persistedAttempt) ? persistedAttempt : 0;
			const receiptConfirmationPending = isReceiptConfirmationPendingError(error);
			// A `pending` verdict on either checkpoint kind is positive evidence that the server is still
			// finalizing THIS exact object. Target receipts arrive after a direct PUT; completion receipts
			// arrive from a create replay that already reported `ready` or — since LSP-19929 — from
			// contact-admin's multipart rotation the moment the server acknowledges `complete`, while its
			// finalizer is still running. Both ride the lenient settling cadence under the durable ceiling.
			const receiptActivelyFinalizing =
				receiptConfirmationPending &&
				error.receiptState === "pending" &&
				(error.receiptKind === "target" || this.options.completionReceiptFinalizing !== undefined);
			const receiptPendingWithinSettlingCeiling =
				receiptActivelyFinalizing && (await this.isReceiptPendingWithinSettlingCeiling(record));
			// R-0 offline contract: a failure caused by no network costs no attempt. It retains a durable
			// shared probe, while online/visibility/startup may accelerate it immediately.
			const offline = this.isOffline(error);
			// R-2 (owner directive 2026-08-27): an auth failure is a PAUSE, not a verdict. Evidence is
			// consulted first so `ORGANIZATION_DELETED` — a coded 404, the one server-confirmed permanent
			// fact — keeps its terminal, and so an ambiguous receipt is never reclassified as a pause.
			const evidenceTerminal = isEvidenceTerminalFailure(error);
			const authCurrent = await this.readAuthCurrent(record);
			const authGated = !evidenceTerminal && isAuthGatedFailure(error, { authCurrent });
			// Budget-free states never increment the attempt count, record poison or advance a backoff
			// tier. Auth gating mirrors R-0's offline treatment; a confirmed pending receipt uses the
			// separate short settling cadence below until its durable elapsed-time ceiling expires.
			const budgetFree = offline || authGated || receiptPendingWithinSettlingCeiling || isUploadControlUnavailable(error);
			const attempt = budgetFree ? priorAttempt : priorAttempt + 1;
			// A positively confirmed pending receipt is not a failure to diagnose. In particular, do not
			// send it through the unknown-failure classifier: its two-attempt diagnostic threshold would
			// turn normal server finalization into the 60-second slow tier. Once the durable elapsed-time
			// ceiling expires, it deliberately falls through to ordinary charged classification/pacing.
			const classification = receiptPendingWithinSettlingCeiling
				? undefined
				: classifyUploadFailure(error, attempt, {
						authCurrent
					});
			const ambiguousDelivery = isAmbiguousReceiptError(error);
			// R-0 (owner directive 2026-08-26): attempt exhaustion is NO LONGER one of these. The only
			// terminals left are evidence-based — bytes acknowledged by S3 that we then failed to record
			// locally (re-sending would duplicate), an ambiguous receipt (re-sending would resend through
			// an unknown server state), and `classification.terminal` (cancellation and the deterministic
			// 400/410/413/422 rejections). Everything else retries forever on an escalating,
			// minutes-capped backoff. R-2 removed 401/403/404 from `classification.terminal` entirely.
			// Break-it round 1: `evidenceTerminal` used to only suppress the auth-gated pause and was then
			// dropped on the floor, so `ORGANIZATION_DELETED` — a coded 404, which R-2 removed from
			// `classification.terminal` along with every other 404 — retried forever against a deleted
			// organization even though `classifyParkedSmallFileRow` already calls it "evidence".
			// PERSIST-03: a native IndexedDB transaction AbortError after an accepted PUT is a failed local
			// checkpoint, not a user cancel, even though its marker classifies as `cancelled`. Fall through
			// to the ordinary retry and receipt check; an explicit cancel still ends terminally.
			const localCheckpointAbort =
				failedAtLocalCheckpoint &&
				classification?.kind === "cancelled" &&
				(error as { cancelled?: unknown }).cancelled !== true &&
				!(error instanceof UploadCancelledError) &&
				isLocalCheckpointFailure(error, record, this.options.receiptOracle);
			if (localCheckpointAbort) {
				// Silent-failure rule: a local store that keeps aborting retries forever (R-0), so name each retry.
				console.warn("[Upload] Small-file local checkpoint aborted after the server accepted the bytes; retrying the receipt", {
					id: record.id,
					conversationId: record.conversationId,
					retryAttempt: attempt,
					verdict: "local-checkpoint-abort"
				});
			}
			const legacyTerminal =
				!authGated &&
				!localCheckpointAbort &&
				(evidenceTerminal ||
					(!contentPersisted &&
						putSucceeded &&
						!receiptConfirmationPending &&
						!isLocalCheckpointFailure(error, record, this.options.receiptOracle)) ||
					ambiguousDelivery ||
					classification?.terminal === true);
			// LSP-22874: the async opt-in keeps a row with bytes on the device retrying unless the server definitively refused it.
			const terminal =
				isUploadControlUnavailable(error) ||
				(this.options.parkOnlyDefinitiveRefusals
					? !authGated && !localCheckpointAbort && isDefinitiveUploadRefusal(error)
					: legacyTerminal);
			if (legacyTerminal && !terminal) {
				// Silent-failure rule: the verdict this row would have been parked on, so a retry loop is traceable.
				console.warn(
					"[Upload] Small-file upload retrying instead of parking: bytes are on the device and the server did not definitively refuse",
					{
						id: record.id,
						conversationId: record.conversationId,
						retryAttempt: attempt,
						classification: classification?.kind ?? null,
						evidenceTerminal,
						ambiguousDelivery,
						status: typeof (error as { status?: unknown })?.status === "number" ? (error as { status: number }).status : null
					}
				);
			}
			// LSP-18802: the error the STORE receives. For a deterministic rejection this carries the
			// `[UPLOAD_REJECTED]` stamp so the parked-row sweeps re-derive the verdict from `lastError`
			// instead of returning the row to the pool every pass; every other shape is passed through
			// untouched (see `withDurableTerminalRejection` for the exclusions and why).
			// IndexedDB structured-clones Error instances without custom HTTP fields such as `status`.
			// Preserve an owned, typed marker whenever auth is the pause decision so a reload can still
			// identify the row and the next sign-in event can retry publication without another PUT.
			const persistedError = terminal
				? withDurableTerminalRejection(error, classification)
				: authGated
					? formatAuthGatedUploadFailureReason(error)
					: error;
			if (terminal) {
				// Silent-failure rule: this is the other park decision, and until LSP-18802 it wrote nothing
				// at all — the release/100 validation had to reconstruct "parked, then un-parked" from the
				// sweep's own log line. Name which evidence parked the row and whether the durable stamp
				// was applied, because "why is this row still retrying?" is exactly the question the
				// combination answers.
				const errorDetails = error as { status?: unknown; code?: unknown };
				console.warn("[Upload] Small-file upload parked as needs-attention", {
					id: record.id,
					conversationId: record.conversationId,
					retryAttempt: attempt,
					// `isEvidenceTerminalFailure` on the LIVE typed error is also true for a bare status
					// rejection (it ends in `classifyUploadFailure`), so the stamp decision — not that
					// predicate — is what separates "the server sent a typed code" from "only the status
					// says so"; the latter is the case whose durable half was missing.
					verdict:
						persistedError !== error
							? "deterministic-rejection"
							: classification?.kind === "cancelled"
								? "cancelled"
								: evidenceTerminal
									? "evidence-terminal"
									: !contentPersisted && putSucceeded && !receiptConfirmationPending
										? "bytes-sent-but-unrecorded"
										: ambiguousDelivery
											? "ambiguous-receipt"
											: "deterministic-rejection-transport-shaped",
					// No raw message: an S3 body carries reflected keys, bucket names and signed URLs
					// (LSP-19422), and this line fires on every park. The status and a code in the
					// facade's own SCREAMING_SNAKE shape are the whole diagnostic.
					status: typeof errorDetails.status === "number" ? errorDetails.status : null,
					code:
						typeof errorDetails.code === "string" && /^[A-Z][A-Z0-9_]{0,63}$/.test(errorDetails.code)
							? errorDetails.code
							: null,
					durableStamp: persistedError !== error ? PERSISTED_TERMINAL_REJECTION_CODE : null
				});
			}
			if (authGated) {
				// Silent-failure rule: this is the park decision the whole directive turns on, and a row
				// that quietly stopped uploading after a 401 is the report it exists to close.
				const authCode = readUploadFacadeErrorCode(error);
				const authMessage =
					authCode === "AUTH_WRONG_TENANT" || authCode === "AUTH_REVOKED"
						? "organization access unavailable; waiting for access (no attempt charged)"
						: "waiting for sign-in (no attempt charged)";
				console.warn(`[Upload] Small-file upload is auth-gated; ${authMessage}`, {
					id: record.id,
					conversationId: record.conversationId,
					retryAttempt: priorAttempt,
					authCurrent,
					durableAuthGatedStatus: this.options.durableAuthGatedStatus === true,
					error: error instanceof Error ? error.message : String(error)
				});
				this.options.onAuthGated?.(record, error);
			}
			let durableRetryAt: number | null = null;
			let retryComputedAt: number | undefined;
			const updated = await this.commitMutation(claim, async (transaction) => {
				// The poison budget keeps recording failures even for a terminal row: its counter is the
				// pacing input, and an explicit user recovery clears it. An OFFLINE or AUTH-GATED failure
				// charges nothing at all — no attempt and no pacing — because neither says anything about
				// whether this particular item is the one that keeps breaking. A positively pending receipt
				// likewise proves active finalization rather than item failure, so it gets no poison credit.
				const poisonRecord = budgetFree ? undefined : await this.poison.recordFailure(record, error, transaction);
				const pacedRetryAt =
					poisonRecord === undefined
						? undefined
						: computePoisonPacedUntil({ ...poisonRecord, lastFailureAt: this.now() }, this.now());
				durableRetryAt = terminal
					? null
					: this.nextRetryAt({
							attempt,
							offline,
							authGated,
							receiptPendingWithinSettlingCeiling,
							classification,
							pacedRetryAt
						});
				// LSP-20948: when the retry time was chosen, so `scheduleRetry` can tell "the write below took
				// longer than the backoff" (fire now) from "the policy asked for no wait" (bounded wave).
				retryComputedAt = this.now();
				return this.options.store.updateRetry(
					record.id,
					{
						retryAt: durableRetryAt,
						retryAttempt: attempt,
						// R-2: `auth-gated` is only ever the DURABLE status where the host's own
						// `listPending` admits it (see `durableAuthGatedStatus`). Everywhere else the row
						// stays `pending` — still schedulable, still paced on the slow tier, still
						// budget-free — because a status a store does not enumerate is an unschedulable
						// park, and that is the defect this directive removes rather than introduces.
						//
						// `contentPersisted` OUTRANKS the auth-gated status, and that precedence is
						// load-bearing rather than cosmetic: once `markUploaded` has committed, S3 holds the
						// bytes and only the server-side publication is outstanding. `uploaded` is the
						// checkpoint that makes the next pass take the completion-receipt branch
						// (`contentPersisted` is re-read from `record.status === "uploaded"` at the top of
						// `uploadOne`), so overwriting it with `auth-gated` would send the row back through
						// presign + re-PUT of bytes the server already accepted — a duplicate upload
						// manufactured by the very directive that exists to protect them. An auth-gated
						// publication failure therefore keeps `uploaded` and is re-driven by `retryAt`
						// alone; `onAuthGated` has already fired for the UI either way.
						status: terminal
							? "needs-attention"
							: contentPersisted
								? "uploaded"
								: authGated && this.options.durableAuthGatedStatus === true
									? "auth-gated"
									: "pending",
						error: persistedError
					},
					record,
					transaction
				);
			});
			if (!terminal && updated !== false) {
				this.scheduleRetry(durableRetryAt, {
					source: "failure-write",
					id: record.id,
					conversationId: record.conversationId,
					computedAt: retryComputedAt
				});
			}
			if (!terminal && !offline && classification?.kind === "refresh" && this.options.retryRefreshImmediately && !this.destroyed) {
				void this.drain();
			}
		} finally {
			await claim?.release();
		}
	}

	/**
	 * The lane's single receipt resolution. It owns everything the adapters used to duplicate:
	 * the writer-claim recheck either side of the network read, result validation, and the bounded
	 * re-check of a still-finalizing server.
	 *
	 * Lease safety: the seal wait runs INSIDE the writer claim, so every poll iteration calls
	 * `isClaimCurrent`, which renews the lease before it re-reads. The lease is therefore refreshed at
	 * least once per `receiptSealPollMs` for the whole wait instead of being held, unrenewed, across it.
	 * A renewal that fails ends the wait immediately with `unknown` and writes nothing.
	 *
	 * The wait applies to `target` checkpoints only. A `completion` receipt is only ever checkpointed
	 * from a create replay that ALREADY reported `ready`, so a later non-ready read means the server
	 * moved backwards — an anomaly to surface, not a finalization to wait out.
	 */
	private async resolveReceipt(
		query: SmallFileReceiptQuery,
		claim: SmallFileUploadClaim | undefined,
		phase: string
	): Promise<SmallFileReceiptState> {
		const { record } = query;
		const waitMs = query.kind === "target" ? Math.max(0, this.options.receiptSealWaitMs ?? 0) : 0;
		const pollMs = Math.max(1, this.options.receiptSealPollMs ?? DEFAULT_RECEIPT_SEAL_POLL_MS);
		// Real clock on purpose: `now` is injectable and several callers pin it to a constant, which
		// would make a wall-clock deadline unreachable.
		const deadline = Date.now() + waitMs;
		for (;;) {
			if (await this.hasLostUploadOwnership(claim, record, `before-${phase}`)) return "unknown";
			const state = await this.options.receiptOracle(query);
			if (await this.hasLostUploadOwnership(claim, record, `after-${phase}`)) return "unknown";
			if (state !== "received" && state !== "absent" && state !== "pending" && state !== "unknown") {
				throw new Error("Facade receipt oracle returned an invalid result");
			}
			if (state !== "pending") {
				if (state === "received" || state === "absent") await this.clearReceiptSettlingWindow(record);
				return state;
			}
			if (Date.now() >= deadline) {
				// The budget is spent, not the upload or its writer lease. Keep the authoritative
				// `pending` verdict so the next scheduled pass polls this checkpoint without re-PUTting.
				return "pending";
			}
			await this.sleep(pollMs);
		}
	}

	/**
	 * A receipt oracle's `pending` answer is positive evidence that this exact object is still being
	 * finalized, so it gets a short, budget-free retry while the durable settling window is open.
	 *
	 * The window is deliberately the same reload-surviving clock used by target-admission settling.
	 * A missing/broken durable port falls back to ordinary charged pacing: fast polling without a
	 * reload-proof ceiling would recreate the unbounded loop this guard exists to prevent.
	 */
	private async isReceiptPendingWithinSettlingCeiling(record: SmallFileRecord): Promise<boolean> {
		const store = this.options.store;
		if (!store.readSettlingWindow || !store.armSettlingWindow) return false;
		const observedAt = this.now();
		try {
			const persistedOpenedAt = await store.readSettlingWindow(record.id);
			let openedAt = persistedOpenedAt;
			if (typeof openedAt !== "number" || !Number.isFinite(openedAt) || openedAt > observedAt) {
				openedAt = observedAt;
				await store.armSettlingWindow(record.id, openedAt, record);
				record.settlingConflictSince = openedAt;
			}
			const elapsedMs = Math.max(0, observedAt - openedAt);
			if (elapsedMs < SETTLING_CONFLICT_CEILING_MS) return true;
			console.warn("[Upload] Small-file receipt stayed pending past its settling ceiling; returning to normal retry pacing", {
				id: record.id,
				conversationId: record.conversationId,
				elapsedMs,
				ceilingMs: SETTLING_CONFLICT_CEILING_MS
			});
			return false;
		} catch (windowError) {
			console.warn("[Upload] Small-file receipt settling window could not be persisted; returning to normal retry pacing", {
				id: record.id,
				conversationId: record.conversationId,
				error: windowError instanceof Error ? windowError.message : String(windowError)
			});
			this.options.onDrainError?.(windowError);
			return false;
		}
	}

	/** Positive receipt evidence closes the shared settling episode so a later one gets a fresh window. */
	private async clearReceiptSettlingWindow(record: SmallFileRecord): Promise<void> {
		const store = this.options.store;
		// The listed record is the durable projection and the arm path keeps it synchronized. Using
		// that known value avoids adding a storage round trip to every healthy receipt confirmation.
		const openedAt = record.settlingConflictSince;
		if (typeof openedAt !== "number" || !Number.isFinite(openedAt)) return;
		try {
			await store.clearSettlingWindow?.(record.id, record);
			record.settlingConflictSince = undefined;
		} catch (windowError) {
			console.warn("[Upload] Small-file receipt settled but its durable settling window could not be cleared", {
				id: record.id,
				conversationId: record.conversationId,
				error: windowError instanceof Error ? windowError.message : String(windowError)
			});
			this.options.onDrainError?.(windowError);
		}
	}

	/**
	 * R-0's schedule for a non-terminal failure. Three tiers, highest wins:
	 * - receipt settling: a fixed short cadence, isolated from attempt and poison curves.
	 * - offline: shared fallback probe plus the existing online/visibility/startup wakes.
	 * - fast: the existing exponential-with-jitter curve (1s -> 30s), used while the item is still
	 *   inside `slowRetryAfterAttempts` and its durable poison budget.
	 * - slow: the same curve moved into the minutes range (60s -> 15min), used once the attempt count
	 *   passes `slowRetryAfterAttempts`, once the classifier asks for pacing (a repeated opaque
	 *   failure), or once the durable poison budget's own pacing window is longer than either.
	 *
	 * No tier ever returns "never". That is the whole directive: pacing replaces parking.
	 */
	private nextRetryAt(input: {
		attempt: number;
		offline: boolean;
		authGated: boolean;
		receiptPendingWithinSettlingCeiling: boolean;
		classification: { kind: string; slowTier?: boolean } | undefined;
		pacedRetryAt: number | undefined;
	}): number | null {
		const nowMs = this.now();
		if (input.receiptPendingWithinSettlingCeiling) return nowMs + DEFAULT_RECEIPT_SETTLING_RETRY_MS;
		if (input.offline) return computeRetryAt({ attempt: input.attempt, nowMs, offline: true });
		// R-2 property 2's second half: "until then they still pace on the shared retry cadence (at most 30s) so
		// unobserved recovery is picked up anyway". Computed from the UNCHANGED attempt count — the
		// failure cost no budget, so the curve must not be walked forward by it either — and deliberately
		// NOT floored by `pacedRetryAt`, because no poison was recorded for this failure.
		if (input.authGated) {
			return computeSlowTierRetryAt({
				attempt: input.attempt,
				nowMs,
				random: this.options.random
			});
		}
		if (input.classification?.kind === "refresh" && this.options.retryRefreshImmediately) {
			// Break-it round 1 (D3): "retry the refresh immediately" used to mean `retryAt: null`
			// unconditionally, which is "due now, forever". Combined with R-0 removing the attempt-count
			// terminal, that left the interactive refresh path with NO bound at all — a zero-delay
			// presign+PUT storm the moment a target kept coming back expired. The immediate retry is
			// still immediate; it just cannot outrun this item's own pacing window.
			return input.pacedRetryAt !== undefined && input.pacedRetryAt > nowMs ? input.pacedRetryAt : null;
		}
		const slowTier =
			input.classification?.slowTier === true ||
			(this.slowRetryAfterAttempts !== undefined && input.attempt >= this.slowRetryAfterAttempts);
		const requested = this.options.retryDelayMs
			? this.options.retryDelayMs(input.attempt, nowMs)
			: (slowTier ? computeSlowTierRetryAt : computeRetryAt)({
					attempt:
						slowTier && this.slowRetryAfterAttempts !== undefined ? input.attempt - this.slowRetryAfterAttempts : input.attempt,
					nowMs,
					random: this.options.random
				});
		const scheduled = requested === null
			? null
			: Math.min(requested, (Number.isFinite(nowMs) ? nowMs : Date.now()) + DEFAULT_RETRY_MAX_DELAY_MS);
		// A host-supplied `retryDelayMs` owns the fast tier, but it must not be able to schedule an
		// over-ceiling item sooner than the durable pacing window that protects its siblings.
		if (input.pacedRetryAt === undefined) return scheduled;
		if (scheduled === null) return input.pacedRetryAt > nowMs ? input.pacedRetryAt : null;
		return Math.max(scheduled, input.pacedRetryAt);
	}

	/**
	 * LSP-19929: the error a non-ready COMPLETION receipt raises. Under `completionReceiptFinalizing`,
	 * an `absent` read counts a strike and `absentStrikes` consecutive strikes make the loss terminal;
	 * any other verdict resets the count. Without it every verdict stays the retryable pending error.
	 */
	private completionReceiptVerdictError(record: SmallFileRecord, receipt: Exclude<SmallFileReceiptState, "received">): Error {
		const strikes = this.options.completionReceiptFinalizing?.absentStrikes;
		if (strikes !== undefined && strikes > 0) {
			if (receipt === "absent") {
				const count = (this.completionReceiptAbsentStrikes.get(record.id) ?? 0) + 1;
				this.completionReceiptAbsentStrikes.set(record.id, count);
				if (count >= strikes) {
					this.completionReceiptAbsentStrikes.delete(record.id);
					return completionReceiptLostError();
				}
			} else {
				this.completionReceiptAbsentStrikes.delete(record.id);
			}
		}
		return receiptConfirmationPendingError(receipt, "completion");
	}

	/** A response wins; without one, either the device hint or an error marker can prove offline. */
	private isOffline(error: unknown): boolean {
		return isOfflineUploadFailure(error, this.hostOffline());
	}

	private hostOffline(): boolean {
		return this.options.isOffline ? this.options.isOffline() : isBrowserOffline();
	}

	/**
	 * Arms the lane's single retry timer for the earliest known `retryAt`.
	 *
	 * LSP-20948: every caller computes `retryAt` as a future time and then awaits something (the failure
	 * write, a poison-budget read) before it gets here. When that await outlasts the backoff — a slow
	 * IndexedDB write on a busy device, 400 ms against a 250 ms first retry — the time has already passed.
	 * This used to return without arming anything, and since the engine never re-scans just because a lane
	 * freed, the row sat due with no wake until a reload or an `online`/visibility event. A past retry now
	 * wakes the lane on a fresh task (after the row's own in-flight attempt settles, when it is still
	 * inside one), so it still enters through `drain()`: the engine's in-flight de-duplication, the per-row
	 * writer claim, the poison pacing and each store's terminal filter all apply exactly as they do to an
	 * on-time retry. Nothing runs inline.
	 *
	 * The one exception is a policy that itself asked for no wait (`retryAt <= computedAt`, e.g. a host
	 * `retryDelayMs` returning `now`): that row is admitted by the next wake, as before, so an immediately
	 * rescheduled failure cannot become a busy loop.
	 */
	private scheduleRetry(retryAt: number | null, lateContext?: SmallFileRetryScheduleContext): void {
		const configuredNow = this.now();
		const now = Number.isFinite(configuredNow) ? configuredNow : Date.now();
		if (retryAt === null) return;
		if (!Number.isFinite(retryAt)) {
			// A non-finite time (a host policy fed a broken clock) would arm `setTimeout(NaN)`, i.e. zero delay,
			// on every failure: a hot loop. Arm nothing; discovery already admits a non-finite `retryAt` as due,
			// so the next wake picks the row up, the same bounded wave a zero-delay policy gets.
			console.warn("[Upload] Small-file retry time is not a finite number; not arming a retry timer", {
				id: lateContext?.id ?? null,
				conversationId: lateContext?.conversationId ?? null,
				source: lateContext?.source ?? "pacing",
				retryAt
			});
			return;
		}
		if (retryAt <= now) {
			if (lateContext?.computedAt !== undefined && retryAt <= lateContext.computedAt) return;
			console.warn("[Upload] Small-file retry time passed before its timer was armed; retrying on the next task", {
				id: lateContext?.id ?? null,
				conversationId: lateContext?.conversationId ?? null,
				source: lateContext?.source ?? "pacing",
				lateMs: now - retryAt
			});
			// The failure write (and the in-attempt pacing check) run INSIDE the row's own attempt, which still
			// has its writer claim to release. A drain request now would find the row in flight; the engine then
			// re-scans only once EVERY lane has settled, so the retry would wait behind a sibling's long PUT.
			// Wake once this attempt has settled instead — still a fresh task, still through `drain()`.
			const inFlightAttempt = lateContext?.id === undefined ? undefined : this.inFlightUploads.get(lateContext.id);
			if (inFlightAttempt) {
				const wakeAfterAttempt = () => {
					globalThis.setTimeout(() => {
						if (!this.destroyed) void this.drain();
					}, 0);
				};
				void inFlightAttempt.then(wakeAfterAttempt, wakeAfterAttempt);
				return;
			}
		}
		if (this.retryTimerAt !== undefined && this.retryTimerAt <= retryAt) return;
		if (this.retryTimer) globalThis.clearTimeout(this.retryTimer);
		this.retryTimerAt = retryAt;
		this.retryTimer = globalThis.setTimeout(
			() => {
				this.retryTimer = undefined;
				this.retryTimerAt = undefined;
				if (!this.destroyed) void this.drain();
			},
			Math.max(0, retryAt - now)
		);
	}

	/**
	 * The host's credential verdict, as the strongest evidence tier for the auth classification.
	 *
	 * A gate that throws or hangs must not decide anything, so a failure resolves to `undefined`
	 * ("unknown") rather than to either verdict, and it is logged with the row id: an auth-recovery
	 * port that has silently stopped answering would otherwise change classification behavior with no
	 * trace at all.
	 */
	private async readAuthCurrent(record: SmallFileRecord): Promise<boolean | undefined> {
		if (this.authGate === noopUploadAuthGate) return undefined;
		try {
			return await this.authGate.isAuthCurrent();
		} catch (error) {
			console.warn("[Upload] Small-file auth gate could not report credential state; classifying from the failure alone", {
				id: record.id,
				conversationId: record.conversationId,
				error
			});
			return undefined;
		}
	}

	private sleep(milliseconds: number): Promise<void> {
		if (this.options.sleep) return this.options.sleep(milliseconds);
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	}

	private hasPublicationTransition(): boolean {
		return Boolean(this.options.publish && this.options.store.markPublished);
	}

	private async clearPoisonFailureInCommit(record: SmallFileRecord, claim: SmallFileUploadClaim | undefined): Promise<void> {
		await this.commitMutation(claim, async (transaction) => {
			await this.poison.clearFailure(record, transaction);
			return true;
		});
	}

	private async markUploaded(record: SmallFileRecord, claim: SmallFileUploadClaim | undefined): Promise<boolean | void> {
		return this.commitMutation(claim, async (transaction) => {
			const marked = await this.options.store.markUploaded(record.id, record, transaction);
			if (marked !== false) this.completionReceiptAbsentStrikes.delete(record.id);
			if (marked !== false && !this.hasPublicationTransition()) await this.poison.clearFailure(record, transaction);
			return marked;
		});
	}

	private async uploadObject(record: SmallFileRecord, claim: SmallFileUploadClaim | undefined): Promise<boolean> {
		const objectUploads = this.options.objectUploads;
		const checkpoint = this.options.store.checkpointObjectUpload;
		if (!objectUploads || !checkpoint || record.target || record.completionReceipt)
			throw uploadControlError("Object upload has no durable source owner");
		const ensureCurrent = async () => {
			if (await this.hasLostUploadOwnership(claim, record, "object-transfer")) throw new Error("Upload writer ownership changed");
		};
		const save = async (choice: SmallFileObjectControl): Promise<SmallFileObjectControl> => {
			const saved = await this.commitMutation(claim, (transaction) => checkpoint(record.id, choice, record, transaction));
			if (!saved) throw new Error("Upload source checkpoint lost writer ownership");
			Object.assign(record, saved);
			return saved;
		};
		selectSmallFileObjectControl(record, record);
		const client = objectUploads.client();
		const request = objectUploads.createRequest(record);
		const control = await prepareKnownSizeUploadSource({
			client,
			scope: request,
			row: record,
			unbound: record.contractVersion !== 2,
			byteSize: record.payload.size,
			contentType: record.fileType,
			chunkSize: CHUNK_SIZE,
			totalParts: Math.ceil(record.payload.size / CHUNK_SIZE),
			readPart: async (offset, size) => record.payload.slice(offset, offset + size),
			commitChoice: save
		});
		if (!control) return false;
		await ensureCurrent();
		if (record.objectAttempt?.takeGeneration) {
			try {
				const snapshot = await readKnownSizeUploadReceipt(
					client,
					{ ...record.objectAttempt, takeGeneration: record.objectAttempt.takeGeneration },
					control
				);
				await ensureCurrent();
				if (snapshot.transferVerified) return true;
			} catch (error) {
				if (
					!objectUploads.waitForProcessing ||
					isEvidenceTerminalFailure(error) ||
					isUploadControlUnavailable(error) ||
					isAuthGatedFailure(error)
				)
					throw error;
				console.warn("[Upload] Saved object receipt is unreadable; retaining source for another observation", { id: record.id });
				throw receiptConfirmationPendingError("unknown", "completion");
			}
		}

		let target;
		try {
			target = await createKnownSizeUploadTarget(
				client,
				request,
				control,
				this.abortController.signal,
				async ({ snapshot, control }) => {
					await save({
						contractVersion: 2,
						protocolSelectionPending: false,
						objectUploadJson: JSON.stringify(control),
						...(snapshot.snapshotId ? { savedSnapshotId: snapshot.snapshotId } : {}),
						objectAttempt: {
							registryId: snapshot.id,
							attemptId: snapshot.attemptId,
							generation: snapshot.generation,
							takeGeneration: snapshot.takeGeneration
						}
					});
				}
			);
		} catch (error) {
			if (!isUploadAlreadyCompleteAtCreate(error)) throw error;
			await ensureCurrent();
			return true;
		}
		for (const part of target.objectUpload!.object.parts) {
			await ensureCurrent();
			const envelope = target.envelopes[part.ordinal - 1];
			if (!envelope || envelope.ordinal !== part.ordinal) throw uploadControlError("Object target grid is incomplete");
			const source = record.payload.slice(part.offset, part.offset + part.byteSize);
			// Safari may send an empty disk-backed Blob after suspension; retain the durable original.
			const payload = new Blob([new Uint8Array(await readBlobBytes(source))], { type: record.fileType });
			await ensureCurrent();
			try {
				await this.options.transport.put(envelope.url, payload, {
					signal: this.abortController.signal,
					headers: envelope.headers,
					requireServerReceipt: true
				});
			} catch (error) {
				await ensureCurrent();
				// Includes a write-once 412 or lost PUT reply: only the exact verified receipt permits adoption.
				if (!(await knownSizePartReceipt(client, target, part.ordinal))) throw error;
				continue;
			}
			await ensureCurrent();
			if (!(await knownSizePartReceipt(client, target, part.ordinal))) throw receiptConfirmationPendingError("pending", "target");
		}
		await ensureCurrent();
		await completeKnownSizeUpload(client, target);
		await ensureCurrent();
		return true;
	}

	private async publishOne(record: SmallFileRecord, claim: SmallFileUploadClaim | undefined): Promise<void> {
		if (!this.options.publish || !this.options.store.markPublished) return;
		if (await this.hasLostUploadOwnership(claim, record, "publish-start")) return;
		const uploadedRecord: SmallFileRecord = {
			...record,
			status: "uploaded",
			publicationState: "pending"
		};
		await this.options.publish(uploadedRecord);
		// The callback is at-least-once. A stale owner may have received an
		// acknowledgement but must never clear a replacement's outbox row.
		if (await this.hasLostUploadOwnership(claim, record, "publish-complete")) return;
		await this.commitMutation(claim, async (transaction) => {
			const marked = await this.options.store.markPublished!(record.id, record, transaction);
			if (marked !== false) await this.poison.clearFailure(record, transaction);
			return marked;
		});
	}

	/**
	 * LSP-19912: the one place a small-file item can be told `UPLOAD_OPERATION_IN_PROGRESS` by the
	 * server's target-admission gate, so the one place the ceiling on that retryable-forever verdict
	 * belongs. Every host lane inherits it here rather than re-implementing it, which is what the
	 * finding was: the ceiling existed in contact-admin's lane only, while contact-async's photo
	 * lane and contact-live's snapshot lane reach the identical server code through the identical
	 * shared classifier.
	 *
	 * A store with no durable settling window keeps the prior unbounded behavior; see
	 * `SmallFileStore.readSettlingWindow` for why an in-memory clock is not offered as a fallback.
	 */
	private async presignBoundedBySettlingCeiling(
		record: SmallFileRecord,
		payloadDigest: () => Promise<string>
	): Promise<string | SmallFilePresignTarget> {
		// Organization sources select v2 before admission and never enter conversation-only legacy wire.
		if (!record.conversationId) throw uploadControlError("Legacy small-file upload has no conversation scope");
		const conversationId = record.conversationId;
		const presign = () =>
			this.options.wire.presign({
				id: record.id,
				conversationId,
				fileDetails: [
					{
						originalFileName: record.fileName,
						fileExtension: extensionFromMime(record.fileType, record.fileName),
						fileType: record.fileType,
						idempotencyId: record.idempotencyId
					}
				],
				type: record.category,
				projectChecklistItemId: record.projectChecklistItemId,
				metadata: record.metadata,
				expectedBytes: record.payload.size,
				payloadDigest
			});
		const store = this.options.store;
		if (!store.readSettlingWindow || !store.armSettlingWindow) return presign();
		return withSettlingCeiling(presign, {
			// The record was projected from its durable row when this drain listed it, so the success
			// path needs no store access at all for the overwhelming majority of items that never saw
			// a conflict. That matters here specifically: an extra round trip between "target minted"
			// and "target committed" is long enough for a second drain to mint a second attempt.
			knownOpenedAt: () => record.settlingConflictSince,
			window: {
				read: () => store.readSettlingWindow!(record.id),
				// The in-memory record is kept in step with the durable write so `knownOpenedAt` stays
				// truthful for a later attempt on this same record instance, whatever a host's own
				// projection does.
				arm: async (openedAt) => {
					await store.armSettlingWindow!(record.id, openedAt, record);
					record.settlingConflictSince = openedAt;
				},
				// A presign that succeeded is positive evidence the admission gate let this row
				// through, so the episode the window was measuring is over. A row presigns again on
				// every rotation retry, every re-enqueue and every reload-driven drain, so a window
				// left armed would terminalize the NEXT, unrelated conflict on sight.
				disarm: async () => {
					await store.clearSettlingWindow?.(record.id, record);
					record.settlingConflictSince = undefined;
				}
			},
			now: () => this.now(),
			onExpired: ({ elapsedMs, ceilingMs }) => {
				console.warn("[Upload] Small-file upload stayed in the server settling window past its ceiling", {
					id: record.id,
					conversationId: record.conversationId,
					elapsedMs,
					ceilingMs
				});
			},
			onSettled: ({ elapsedMs }) => {
				console.info("[Upload] Small-file upload left the server settling window; its ceiling restarts from the next conflict", {
					id: record.id,
					conversationId: record.conversationId,
					elapsedMs
				});
			},
			onWindowError: (windowError) => {
				// A durable-store failure must never upgrade a retryable settling verdict into a
				// terminal one; the next drain re-observes the conflict and re-arms the window. Nor may
				// it fail a presign that already succeeded — the close is retried by the next one.
				console.warn(
					"[Upload] Small-file settling-window checkpoint failed",
					{ id: record.id, conversationId: record.conversationId },
					windowError
				);
			}
		});
	}

	private async commitMutation<T>(
		claim: SmallFileUploadClaim | undefined,
		effect: (transaction?: DurableUploadTransaction) => Promise<T>
	): Promise<T | false> {
		if (!claim?.commitIfWriterOwns) return effect();
		const outcome = await claim.commitIfWriterOwns(effect);
		return outcome.kind === "applied" ? outcome.value : false;
	}

	/**
	 * Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures"). Every
	 * fail-condition that ends an upload attempt without an error names itself here, so a stalled
	 * Live snapshot is one Datadog search — scoped by `conversationId`, which LSP-20746 showed was
	 * the identifier every search is scoped by and the one these lines never carried.
	 */
	private warnUploadEndedEarly(condition: string, record: SmallFileRecord, target?: SmallFilePresignTarget): void {
		console.warn(`[Upload] Small-file upload attempt ended early: ${condition}`, {
			condition,
			id: record.id,
			conversationId: record.conversationId,
			registryId: target?.registryId ?? record.target?.registryId ?? null,
			attemptId: target?.attemptId ?? record.target?.attemptId ?? null,
			retryAttempt: record.retryAttempt ?? 0
		});
	}

	/**
	 * The `destroyed || !isClaimCurrent(...)` guard repeated across the upload path, with each
	 * outcome logged once. `isClaimCurrent` owns the stale-lease line so a lost lease is reported
	 * from one place for every phase; destruction is reported here because it short-circuits it.
	 */
	private async hasLostUploadOwnership(
		claim: SmallFileUploadClaim | undefined,
		record: SmallFileRecord,
		phase: string
	): Promise<boolean> {
		if (this.destroyed) {
			this.warnUploadEndedEarly(`lane-destroyed-at-${phase}`, record);
			return true;
		}
		return !(await this.isClaimCurrent(claim, record.id, phase, record));
	}

	private async isClaimCurrent(
		claim: SmallFileUploadClaim | undefined,
		id: string,
		phase: string,
		record?: SmallFileRecord
	): Promise<boolean> {
		if (!claim) return true;
		const reportStaleOwner = (reason: string) => {
			this.options.onStaleOwner?.({ id, phase });
			if (record) this.warnUploadEndedEarly(`${reason}-at-${phase}`, record);
			else console.warn(`[Upload] Small-file upload attempt ended early: ${reason}-at-${phase}`, { id, phase });
		};
		if (claim.renew && !(await claim.renew())) {
			reportStaleOwner("writer-lease-renewal-failed");
			return false;
		}
		const current = await claim.isCurrent();
		if (!current) reportStaleOwner("writer-lease-no-longer-current");
		return current;
	}

	destroy(): void {
		this.destroyed = true;
		this.completionReceiptAbsentStrikes.clear();
		if (this.retryTimer) globalThis.clearTimeout(this.retryTimer);
		this.retryTimer = undefined;
		this.retryTimerAt = undefined;
		this.unsubscribeAuthGate();
		this.drainEngine.dispose();
		this.abortController.abort();
	}

	private now(): number {
		return this.options.now?.() ?? Date.now();
	}
}
